import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { CheckCircle, XCircle, RefreshCw, ChevronDown, ChevronUp, AlertTriangle, Clock, Users, ClipboardCheck, QrCode, Printer, CalendarDays } from 'lucide-react';
import DriverAvatar, { fetchDriverPhotos } from '../components/DriverPhoto';
import { periodGrid } from '../utils/periods';

// ── QR print helper (popup window — bypasses dark theme CSS variables) ────────
function qrImageUrl(data, size = 200) {
  return `https://api.qrserver.com/v1/create-qr-code/?data=${encodeURIComponent(data)}&size=${size}x${size}&ecc=M&margin=2`;
}
function openPrintWindow(depotsToprint) {
  const qrSrc = url => `https://api.qrserver.com/v1/create-qr-code/?data=${encodeURIComponent(url)}&size=280x280&ecc=M&margin=2`;
  const cards = depotsToprint.map(depot => `
    <div class="card">
      <div class="card-header">
        <div class="pin">📍</div>
        <div>
          <div class="depot-name">${depot.name.toUpperCase()} DEPOT</div>
          <div class="depot-addr">${depot.address}</div>
        </div>
      </div>
      <div class="section">
        <div class="step-row"><div class="step-badge">1</div><div><div class="step-title">SCAN THIS QR CODE</div><div class="step-sub">Point your phone camera at the code — the shift portal opens automatically. Valid today only.</div></div></div>
        <div class="qr-wrap"><img src="${qrSrc(depot.url)}" width="280" height="280" alt="QR" /></div>
      </div>
      <div class="divider"></div>
      <div class="section">
        <div class="step-row"><div class="step-badge">2</div><div><div class="step-title">LOG IN WITH YOUR SCANNER CREDENTIALS</div><div class="step-sub">Please use your assigned scanner credentials to log in.</div></div></div>
        <div class="creds">
          <div class="cred-row"><div class="cred-label">LOGIN NAME</div><div class="cred-field">Your Login</div></div>
          <div class="cred-row"><div class="cred-label">SCANNER PIN</div><div class="cred-field">Your Scanner PIN</div></div>
        </div>
        <div class="cred-note">⚠ Your login and PIN were provided by your depot manager. Contact management if you have not received them.</div>
      </div>
      <div class="divider"></div>
      <div class="section">
        <div class="step-row"><div class="step-badge">3</div><div><div class="step-title">PRESS START SHIFT</div><div class="step-sub">The Start Shift button activates once your QR scan and GPS location are both verified. Do not leave the depot gate before pressing Start Shift.</div></div></div>
      </div>
      <div class="card-footer"><span class="logo">HOLLYPAUL</span><span class="tagline">Driver Portal — ${depot.name} Gate &nbsp;·&nbsp; hollypaul.com/driver-log</span></div>
    </div>`).join('');
  const html = `<!DOCTYPE html><html><head><meta charset="UTF-8"/><title>Depot QR Codes — HOLLYPAUL</title>
  <style>
    *{box-sizing:border-box;margin:0;padding:0}
    body{font-family:-apple-system,Arial,sans-serif;background:#fff;color:#111;padding:28px}
    .grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(min(360px, 100%),1fr));gap:28px}
    .card{border:2px solid #222;border-radius:12px;overflow:hidden;page-break-inside:avoid;break-inside:avoid}
    .card-header{background:#111;color:#fff;padding:16px 22px;display:flex;align-items:center;gap:14px}
    .pin{font-size:1.6rem}.depot-name{font-size:1.3rem;font-weight:800;letter-spacing:.06em}
    .depot-addr{font-size:.78rem;color:#bbb;margin-top:3px}
    .section{padding:18px 22px}.divider{height:1px;background:#ddd;margin:0 22px}
    .step-row{display:flex;align-items:flex-start;gap:14px;margin-bottom:14px}
    .step-badge{min-width:28px;height:28px;border-radius:50%;background:#111;color:#fff;font-size:.85rem;font-weight:800;display:flex;align-items:center;justify-content:center;flex-shrink:0;margin-top:1px}
    .step-title{font-size:.9rem;font-weight:800;letter-spacing:.04em;margin-bottom:4px}
    .step-sub{font-size:.78rem;color:#555;line-height:1.5}
    .qr-wrap{display:flex;justify-content:center;padding:4px 0 6px}
    .qr-wrap img{border:1px solid #ddd;border-radius:8px;padding:8px;background:#fff;display:block}
    .creds{display:flex;flex-direction:column;gap:10px;margin-bottom:12px}
    .cred-row{display:flex;align-items:center;border:1.5px solid #222;border-radius:8px;overflow:hidden}
    .cred-label{background:#f0f0f0;padding:10px 14px;font-size:.72rem;font-weight:800;letter-spacing:.08em;text-transform:uppercase;color:#333;min-width:130px;border-right:1.5px solid #222}
    .cred-field{padding:10px 16px;font-size:.88rem;color:#777;font-style:italic;flex:1}
    .cred-note{font-size:.72rem;color:#888;line-height:1.5;background:#fafafa;border:1px solid #e0e0e0;border-radius:6px;padding:8px 12px}
    .card-footer{background:#111;color:#fff;padding:11px 22px;display:flex;justify-content:space-between;align-items:center}
    .logo{font-size:.95rem;font-weight:800;letter-spacing:.12em}.tagline{font-size:.7rem;color:#aaa}
    @media print{body{padding:0}.grid{gap:0}.card{border-radius:0;border-width:1px;page-break-after:always}.card:last-child{page-break-after:avoid}}
  </style></head>
  <body><div class="grid">${cards}</div>
  <script>var imgs=document.querySelectorAll('img'),loaded=0;function tryPrint(){loaded++;if(loaded>=imgs.length)window.print();}
  if(!imgs.length)window.print();else imgs.forEach(function(i){if(i.complete)tryPrint();else{i.onload=tryPrint;i.onerror=tryPrint;}})</script>
  </body></html>`;
  const w = window.open('', '_blank', 'width=820,height=1000');
  if (w) { w.document.write(html); w.document.close(); }
}

// A shift the driver finished without their scanner photo because the camera on
// their phone would not take one. The portal writes this marker into
// manager_notes (DriverLogPage: NO_PHOTO_PREFIX) — keep the two in step.
const NO_PHOTO_PREFIX = 'NO SCANNER PHOTO — ';
const isNoPhotoNote = (note) => (note || '').startsWith(NO_PHOTO_PREFIX);

const STATUS_STYLE = {
  PENDING:   { bg: 'rgba(245,158,11,0.15)',  color: 'var(--hp-amber)',  label: 'Pending'   },
  APPROVED:  { bg: 'rgba(34,197,94,0.15)',   color: 'var(--hp-green)',  label: 'Approved'  },
  REJECTED:  { bg: 'rgba(239,68,68,0.15)',   color: 'var(--hp-red)',    label: 'Rejected'  },
  SUPERSEDED:{ bg: 'rgba(136,136,160,0.15)', color: 'var(--hp-dim)',    label: 'Superseded'},
};

const TYPE_STYLE = {
  ORIGINAL:  { bg: 'rgba(59,130,246,0.12)',  color: '#60a5fa', label: 'Original'  },
  AMENDMENT: { bg: 'rgba(139,92,246,0.15)',  color: '#a78bfa', label: 'Amendment' },
};

// Snap an HH:mm to the nearest quarter hour (ties round up), matching the
// sorters' "Round all to 15". Returns '' for an unparseable time. 23:53 wraps
// to 00:00 — editShiftTimes treats a finish before the start as next-day.
function roundHHMMTo15(t) {
  const m = /^(\d{1,2}):(\d{2})/.exec((t || '').toString().trim());
  if (!m) return '';
  const mins = Number(m[1]) * 60 + Number(m[2]);
  if (!Number.isFinite(mins)) return '';
  const x = (((Math.round(mins / 15) * 15) % 1440) + 1440) % 1440;
  return `${String(Math.floor(x / 60)).padStart(2, '0')}:${String(x % 60).padStart(2, '0')}`;
}

function Badge({ style, text }) {
  return (
    <span style={{
      background: style.bg,
      color: style.color,
      fontSize: '0.7rem',
      fontWeight: 700,
      padding: '3px 8px',
      borderRadius: 20,
      letterSpacing: '0.06em',
      textTransform: 'uppercase',
      whiteSpace: 'nowrap',
    }}>{text || style.label}</span>
  );
}

function StatCard({ icon: Icon, label, value, color }) {
  return (
    <div style={{
      background: 'var(--hp-surface)',
      border: '1px solid var(--hp-border)',
      borderRadius: 'var(--radius-lg)',
      padding: '20px 24px',
      flex: 1,
      minWidth: 140,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
        <Icon size={16} color={color} />
        <span style={{ fontSize: '0.72rem', color: 'var(--hp-dim)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em' }}>{label}</span>
      </div>
      <div style={{ fontSize: '2rem', fontFamily: 'var(--font-display)', color, letterSpacing: '0.04em' }}>{value}</div>
    </div>
  );
}

// Downscale a chosen image to a JPEG data URL (keeps uploads small + legible).
function compressToDataUrl(file, maxEdge = 1400, quality = 0.85) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const ratio = Math.min(1, maxEdge / Math.max(img.width, img.height));
        const w = Math.max(1, Math.round(img.width * ratio));
        const h = Math.max(1, Math.round(img.height * ratio));
        const c = document.createElement('canvas');
        c.width = w; c.height = h;
        c.getContext('2d').drawImage(img, 0, 0, w, h);
        resolve(c.toDataURL('image/jpeg', quality));
      };
      img.onerror = () => reject(new Error('Could not read that image'));
      img.src = reader.result;
    };
    reader.onerror = () => reject(new Error('Could not read that file'));
    reader.readAsDataURL(file);
  });
}

// Admin tool: attach a scanner photo to a driver's record (e.g. a photo the
// driver sent via WhatsApp because their phone couldn't capture it in-app).
function AdminScannerUpload({ personnel, addNotification }) {
  const todayISO = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };
  const [open, setOpen]     = useState(false);
  const [driver, setDriver] = useState('');
  const [date, setDate]     = useState(todayISO());
  const [pic, setPic]       = useState(null);   // { dataUrl, base64, name }
  const [busy, setBusy]     = useState(false);

  const drivers = (personnel || [])
    .map(p => ({ login: (p.loginName || p.login || '').toUpperCase(), name: p.name || p.contactName || '' }))
    .filter(p => p.login)
    .sort((a, b) => a.login.localeCompare(b.login));

  const onPick = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    try {
      const dataUrl = await compressToDataUrl(f);
      setPic({ dataUrl, base64: dataUrl.split(',')[1] || '', name: f.name });
    } catch (err) { addNotification(err.message, 'error'); }
    e.target.value = '';
  };

  const submit = async () => {
    if (!driver || !pic) return;
    setBusy(true);
    try {
      const name = drivers.find(d => d.login === driver)?.name || driver;
      const res = await fetch('/.netlify/functions/scanner-photo', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'upload', driver, driverName: name, date, imageData: pic.base64, contentType: 'image/jpeg', note: 'Uploaded by admin' }),
      });
      const d = await res.json();
      if (!d.ok) throw new Error(d.error || 'Upload failed');
      addNotification(`Scanner photo attached for ${driver} on ${date}`, 'success');
      setPic(null); setDriver('');
    } catch (err) { addNotification('Upload failed: ' + err.message, 'error'); }
    finally { setBusy(false); }
  };

  const inputStyle = { width: '100%', background: 'var(--hp-bg)', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', padding: '9px 12px', color: 'var(--hp-text)', fontSize: '0.88rem', boxSizing: 'border-box' };

  return (
    <div style={{ marginBottom: 24, background: 'var(--hp-surface)', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius-lg)', overflow: 'hidden' }}>
      <button onClick={() => setOpen(o => !o)} style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 18px', background: 'none', border: 'none', cursor: 'pointer' }}>
        <span style={{ fontSize: '0.82rem', fontWeight: 700, color: 'var(--hp-text)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Attach scanner photo for a driver</span>
        {open ? <ChevronUp size={16} color="var(--hp-dim)" /> : <ChevronDown size={16} color="var(--hp-dim)" />}
      </button>
      {open && (
        <div style={{ padding: '0 18px 18px' }}>
          <p style={{ fontSize: '0.78rem', color: 'var(--hp-dim)', margin: '0 0 14px' }}>
            Use this when a driver couldn't take the photo in-app and sent it to you (e.g. via WhatsApp). It's saved against their shift exactly like an in-app upload.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(200px, 100%), 1fr))', gap: 12, alignItems: 'end' }}>
            <div>
              <label style={{ fontSize: '0.7rem', color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', marginBottom: 6 }}>Driver</label>
              <select value={driver} onChange={e => setDriver(e.target.value)} style={inputStyle}>
                <option value="">— Select driver —</option>
                {drivers.map(d => <option key={d.login} value={d.login}>{d.login}{d.name ? ` — ${d.name}` : ''}</option>)}
              </select>
            </div>
            <div>
              <label style={{ fontSize: '0.7rem', color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', marginBottom: 6 }}>Date</label>
              <input type="date" value={date} onChange={e => setDate(e.target.value)} style={inputStyle} />
            </div>
            <div>
              <label style={{ fontSize: '0.7rem', color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', marginBottom: 6 }}>Photo</label>
              <label style={{ ...inputStyle, display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', color: pic ? 'var(--hp-text)' : 'var(--hp-dim)' }}>
                <input type="file" accept="image/*" onChange={onPick} style={{ display: 'none' }} />
                {pic ? `✓ ${pic.name || 'photo selected'}` : 'Choose photo…'}
              </label>
            </div>
          </div>
          {pic && <img src={pic.dataUrl} alt="preview" style={{ marginTop: 12, maxHeight: 160, borderRadius: 'var(--radius)', border: '1px solid var(--hp-border)' }} />}
          <div style={{ marginTop: 14 }}>
            <button onClick={submit} disabled={busy || !driver || !pic}
              style={{ padding: '10px 20px', background: 'var(--hp-gold)', border: 'none', borderRadius: 'var(--radius)', color: 'var(--hp-black)', fontWeight: 800, fontSize: '0.85rem', cursor: (busy || !driver || !pic) ? 'not-allowed' : 'pointer', opacity: (busy || !driver || !pic) ? 0.5 : 1 }}>
              {busy ? 'Uploading…' : 'Attach photo'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// Admin tool: add a whole shift from scratch (driver couldn't activate the
// portal that day). Writes a Driver_Logs row with computed hours.
function AdminAddShift({ addNotification, personnel, by, onAdded }) {
  const todayISO = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };
  const [open, setOpen]     = useState(false);
  const [driver, setDriver] = useState('');
  const [date, setDate]     = useState(todayISO());
  const [start, setStart]   = useState('');
  const [finish, setFinish] = useState('');
  const [brkStart, setBrkStart] = useState('');
  const [brkEnd, setBrkEnd] = useState('');
  const [km, setKm]         = useState('19');
  const [note, setNote]     = useState('');
  const [busy, setBusy]     = useState(false);

  const drivers = (personnel || [])
    .map(p => ({ login: (p.loginName || p.login || '').toUpperCase(), name: p.name || p.contactName || '' }))
    .filter(p => p.login).sort((a, b) => a.login.localeCompare(b.login));

  const submit = async () => {
    if (!driver || !date || !start || !finish) { addNotification('Driver, date, start and finish are required', 'error'); return; }
    setBusy(true);
    try {
      const name = drivers.find(d => d.login === driver)?.name || driver;
      const res = await fetch('/.netlify/functions/appsscript', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'adminAddShift', driver, name, date, start, finish, breakStart: brkStart, breakEnd: brkEnd, totalKm: km !== '' ? Number(km) : 0, note, by: by || 'ADMIN' }),
      });
      const d = await res.json();
      if (!d.success) throw new Error(d.error || 'Add failed');
      addNotification(`Shift added for ${driver} on ${date} — ${d.totalHours} hrs, ${d.totalKm} km`, 'success');
      setDriver(''); setStart(''); setFinish(''); setBrkStart(''); setBrkEnd(''); setNote(''); setKm('19');
      onAdded && onAdded();
    } catch (e) { addNotification('Could not add: ' + e.message, 'error'); }
    finally { setBusy(false); }
  };

  const inputStyle = { width: '100%', background: 'var(--hp-bg)', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', padding: '9px 12px', color: 'var(--hp-text)', fontSize: '0.88rem', boxSizing: 'border-box' };
  const lblStyle = { fontSize: '0.7rem', color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', marginBottom: 6 };

  return (
    <div style={{ marginBottom: 24, background: 'var(--hp-surface)', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius-lg)', overflow: 'hidden' }}>
      <button onClick={() => setOpen(o => !o)} style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 18px', background: 'none', border: 'none', cursor: 'pointer' }}>
        <span style={{ fontSize: '0.82rem', fontWeight: 700, color: 'var(--hp-text)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Add a shift for a driver</span>
        {open ? <ChevronUp size={16} color="var(--hp-dim)" /> : <ChevronDown size={16} color="var(--hp-dim)" />}
      </button>
      {open && (
        <div style={{ padding: '0 18px 18px' }}>
          <p style={{ fontSize: '0.78rem', color: 'var(--hp-dim)', margin: '0 0 14px' }}>
            For a driver who couldn't activate the portal that day. Creates the shift record (hours calculated from start → finish, less any break). It won't let you add a day that already has a shift — edit that one instead.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(150px, 100%), 1fr))', gap: 12 }}>
            <div>
              <label style={lblStyle}>Driver</label>
              <select value={driver} onChange={e => setDriver(e.target.value)} style={inputStyle}>
                <option value="">— Select driver —</option>
                {drivers.map(d => <option key={d.login} value={d.login}>{d.login}{d.name ? ` — ${d.name}` : ''}</option>)}
              </select>
            </div>
            <div><label style={lblStyle}>Date</label><input type="date" value={date} onChange={e => setDate(e.target.value)} style={inputStyle} /></div>
            <div><label style={lblStyle}>Start</label><input type="time" value={start} onChange={e => setStart(e.target.value)} style={inputStyle} /></div>
            <div><label style={lblStyle}>Finish</label><input type="time" value={finish} onChange={e => setFinish(e.target.value)} style={inputStyle} /></div>
            <div><label style={lblStyle}>Break start (opt)</label><input type="time" value={brkStart} onChange={e => setBrkStart(e.target.value)} style={inputStyle} /></div>
            <div><label style={lblStyle}>Break end (opt)</label><input type="time" value={brkEnd} onChange={e => setBrkEnd(e.target.value)} style={inputStyle} /></div>
            <div><label style={lblStyle}>KM</label><input type="number" inputMode="numeric" value={km} onChange={e => setKm(e.target.value)} style={inputStyle} /></div>
            <div><label style={lblStyle}>Note (opt)</label><input type="text" placeholder="e.g. couldn't activate portal" value={note} onChange={e => setNote(e.target.value)} style={inputStyle} /></div>
          </div>
          <div style={{ marginTop: 14 }}>
            <button onClick={submit} disabled={busy || !driver || !start || !finish}
              style={{ padding: '10px 20px', background: 'var(--hp-gold)', border: 'none', borderRadius: 'var(--radius)', color: 'var(--hp-black)', fontWeight: 800, fontSize: '0.85rem', cursor: (busy || !driver || !start || !finish) ? 'not-allowed' : 'pointer', opacity: (busy || !driver || !start || !finish) ? 0.5 : 1 }}>
              {busy ? 'Adding…' : 'Add shift'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// Admin tool: close an unfinished shift on a driver's behalf (driver sent home /
// couldn't submit / forgot to log off). Loads ALL still-active sessions (any date):
// a date picker (default today) scopes the list, and sessions stuck on a past date
// are called out so they can be closed here instead of in the database. Admin sets
// finish time + optional finish odometer; the backend writes the shift log and clears it.
function AdminFinishShift({ addNotification, by, onFinished }) {
  const todayISO = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };
  const nowHHMM = () => { const d = new Date(); return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`; };
  const [open, setOpen]       = useState(false);
  const [all, setAll]         = useState([]);
  const [loading, setLoading] = useState(false);
  const [date, setDate]       = useState(todayISO());
  const [driver, setDriver]   = useState('');
  const [finishTime, setFinishTime] = useState(nowHHMM());
  const [odoEnd, setOdoEnd]   = useState('');
  const [note, setNote]       = useState('');
  const [busy, setBusy]       = useState(false);

  const loadActive = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch('/.netlify/functions/appsscript', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'getActiveShifts' }),
      });
      const d = await res.json();
      if (d.success) setAll(d.shifts || []);
    } catch {} finally { setLoading(false); }
  }, []);
  useEffect(() => { if (open) loadActive(); }, [open, loadActive]);

  const active = all.filter(a => a.date === date);
  const stale = all.filter(a => a.date < todayISO()).sort((x, y) => x.date < y.date ? -1 : x.date > y.date ? 1 : x.driver < y.driver ? -1 : 1);
  const sel = active.find(a => a.driver === driver);
  const fmtStart = (iso) => { const dt = new Date(iso); return isNaN(dt) ? (iso || '—') : dt.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit', hour12: false }); };
  const fmtDate = (d) => { const dt = new Date(d + 'T00:00:00'); return isNaN(dt) ? d : dt.toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' }); };

  const submit = async () => {
    if (!driver) return;
    setBusy(true);
    try {
      const res = await fetch('/.netlify/functions/appsscript', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'adminFinishShift', driver, date, finishTime, odoEnd: odoEnd !== '' ? Number(odoEnd) : '', note, by: by || 'ADMIN' }),
      });
      const d = await res.json();
      if (!d.success) throw new Error(d.error || 'Finish failed');
      addNotification(`Shift finished for ${driver} on ${date} — ${d.totalHours} hrs${d.totalKm ? `, ${d.totalKm} km` : ''}`, 'success');
      setDriver(''); setOdoEnd(''); setNote(''); setFinishTime(nowHHMM());
      await loadActive();
      onFinished && onFinished();
    } catch (e) { addNotification('Could not finish: ' + e.message, 'error'); }
    finally { setBusy(false); }
  };

  const inputStyle = { width: '100%', background: 'var(--hp-bg)', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', padding: '9px 12px', color: 'var(--hp-text)', fontSize: '0.88rem', boxSizing: 'border-box' };
  const lblStyle = { fontSize: '0.7rem', color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', marginBottom: 6 };

  return (
    <div style={{ marginBottom: 24, background: 'var(--hp-surface)', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius-lg)', overflow: 'hidden' }}>
      <button onClick={() => setOpen(o => !o)} style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 18px', background: 'none', border: 'none', cursor: 'pointer' }}>
        <span style={{ fontSize: '0.82rem', fontWeight: 700, color: 'var(--hp-text)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Finish a shift for a driver</span>
        {open ? <ChevronUp size={16} color="var(--hp-dim)" /> : <ChevronDown size={16} color="var(--hp-dim)" />}
      </button>
      {open && (
        <div style={{ padding: '0 18px 18px' }}>
          <p style={{ fontSize: '0.78rem', color: 'var(--hp-dim)', margin: '0 0 14px' }}>
            Closes an unfinished shift on the driver's behalf (e.g. sent home / couldn't submit / forgot to log off). Their start time is kept; enter the finish time and, if you have it, the finish odometer. Pick the shift date first — sessions stuck on a past day are listed below.
          </p>
          {loading && <div style={{ fontSize: '0.82rem', color: 'var(--hp-dim)' }}>Loading active shifts…</div>}
          {!loading && stale.length > 0 && (
            <div style={{ marginBottom: 14, padding: '12px 14px', background: 'rgba(217,164,65,0.08)', border: '1px solid rgba(217,164,65,0.35)', borderRadius: 'var(--radius)' }}>
              <div style={{ fontSize: '0.74rem', fontWeight: 700, color: 'var(--hp-gold)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>
                ⚠ {stale.length} session{stale.length === 1 ? '' : 's'} stuck on a past day — never signed off
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {stale.map(s => (
                  <div key={`${s.driver}|${s.date}`} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, fontSize: '0.8rem', color: 'var(--hp-text)' }}>
                    <span>{fmtDate(s.date)} — <strong>{s.driver}</strong>{s.name && s.name !== s.driver ? ` (${s.name})` : ''} · started {fmtStart(s.shiftStart)}{s.vehicleRego ? ` · ${s.vehicleRego}` : ''}</span>
                    <button onClick={() => { setDate(s.date); setDriver(s.driver); }}
                      style={{ padding: '4px 12px', background: 'none', border: '1px solid var(--hp-gold)', borderRadius: 'var(--radius)', color: 'var(--hp-gold)', fontWeight: 700, fontSize: '0.74rem', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                      Close →
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
          {!loading && (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(180px, 100%), 1fr))', gap: 12 }}>
                <div>
                  <label style={lblStyle}>Shift date</label>
                  <input type="date" value={date} onChange={e => { setDate(e.target.value); setDriver(''); }} style={inputStyle} />
                </div>
                <div>
                  <label style={lblStyle}>Driver (still on shift)</label>
                  <select value={driver} onChange={e => setDriver(e.target.value)} style={inputStyle} disabled={active.length === 0}>
                    <option value="">{active.length === 0 ? `— No unfinished shifts on ${date} —` : '— Select driver —'}</option>
                    {active.map(a => <option key={a.driver} value={a.driver}>{a.driver}{a.name && a.name !== a.driver ? ` — ${a.name}` : ''} (started {fmtStart(a.shiftStart)})</option>)}
                  </select>
                </div>
                <div>
                  <label style={lblStyle}>Finish time</label>
                  <input type="time" value={finishTime} onChange={e => setFinishTime(e.target.value)} style={inputStyle} />
                </div>
                <div>
                  <label style={lblStyle}>Finish odometer (optional)</label>
                  <input type="number" inputMode="numeric" placeholder="leave blank if unknown" value={odoEnd} onChange={e => setOdoEnd(e.target.value)} style={inputStyle} />
                </div>
                <div>
                  <label style={lblStyle}>Note (optional)</label>
                  <input type="text" placeholder="e.g. sent home — couldn't submit" value={note} onChange={e => setNote(e.target.value)} style={inputStyle} />
                </div>
              </div>
              {sel && <div style={{ fontSize: '0.74rem', color: 'var(--hp-dim)', marginTop: 10 }}>Started {fmtStart(sel.shiftStart)}{sel.vehicleRego ? ` · ${sel.vehicleRego}` : ''} — hours are calculated from start to the finish time you enter.</div>}
              <div style={{ marginTop: 14 }}>
                <button onClick={submit} disabled={busy || !driver}
                  style={{ padding: '10px 20px', background: 'var(--hp-gold)', border: 'none', borderRadius: 'var(--radius)', color: 'var(--hp-black)', fontWeight: 800, fontSize: '0.85rem', cursor: (busy || !driver) ? 'not-allowed' : 'pointer', opacity: (busy || !driver) ? 0.5 : 1 }}>
                  {busy ? 'Finishing…' : 'Finish shift'}
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// Period close-out: review every unfinished shift in a pay period at once.
//
// The backlog of never-signed-off sessions is worked a PERIOD at a time, not a day at a
// time, because that is how the money is worked. Pick the period, press Review, and the
// engine reads each open session the way a person would: did the driver close the portal
// that day (then only the leftover session needs clearing); if not, what does the Scanner
// Audit say; if the audit has nothing, say so plainly rather than dress a guess up as a
// finding. Every proposal shows its evidence and every doubt is written out in words —
// "2.7 h against their usual 7.5 h" — so the operator is deciding, not rubber-stamping.
//
// Green rows can go through together; amber and red are one at a time, on purpose.
// Everything written lands PENDING in the approvals list below, so nothing this panel
// does is final until it is approved there too.
function PeriodCloseout({ addNotification, by, onChanged }) {
  const [open, setOpen]       = useState(false);
  const grid = React.useMemo(() => periodGrid({ back: 8, forward: 0 }), []);
  const [periodIdx, setPeriodIdx] = useState(() => Math.max(0, grid.findIndex(p => p.current)));
  const [from, setFrom]       = useState(() => (grid.find(p => p.current) || grid[0] || {}).startISO || '');
  const [to, setTo]           = useState(() => (grid.find(p => p.current) || grid[0] || {}).endISO || '');
  const [driveBack, setDriveBack] = useState(15);
  const [res, setRes]         = useState(null);
  const [loading, setLoading] = useState(false);
  const [edits, setEdits]     = useState({});   // key -> { finish, km, start }
  const [busyKey, setBusyKey] = useState('');
  const [bulk, setBulk]       = useState(null); // { done, total }
  const [done, setDone]       = useState({});   // key -> outcome text
  const [show, setShow]       = useState('all');

  const keyOf = (r) => `${r.driver}|${r.date}`;
  const post = async (body) => {
    const r = await fetch('/.netlify/functions/shift-closeout-sb', {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
    });
    return r.json();
  };

  const review = async () => {
    if (!from || !to) return;
    setLoading(true); setRes(null); setDone({});
    try {
      const j = await post({ action: 'reviewPeriod', from, to, driveBackMins: driveBack });
      if (!j.success) throw new Error(j.error || 'Review failed');
      setRes(j);
      const seed = {};
      (j.rows || []).forEach(r => { seed[keyOf(r)] = { finish: r.finish || '', km: r.estKm != null ? String(r.estKm) : '', start: r.start || '' }; });
      setEdits(seed);
      if (!j.rows.length) addNotification(`No unfinished shifts in ${from} to ${to}`, 'success');
    } catch (e) { addNotification('Review: ' + e.message, 'error'); }
    finally { setLoading(false); }
  };

  // One row. `clearStale` writes nothing; `close` writes the shift log PENDING.
  const settle = async (r) => {
    const k = keyOf(r);
    const e = edits[k] || {};
    setBusyKey(k);
    try {
      let j;
      if (r.action === 'clearStale') {
        j = await post({ action: 'clearStale', driver: r.driver, date: r.date });
        if (!j.success) throw new Error(j.error);
        setDone(d => ({ ...d, [k]: 'Session cleared — the day was already logged.' }));
      } else {
        if (!/^\d{1,2}:\d{2}/.test(e.finish || '')) throw new Error('Enter a finish time first');
        j = await post({
          action: 'closeOne', driver: r.driver, date: r.date, finishTime: e.finish,
          startTime: (e.start && e.start !== r.start) ? e.start : '',
          estKm: e.km !== '' ? Number(e.km) : '', by,
          note: noteFor(r, e),
        });
        if (!j.success) throw new Error(j.error);
        setDone(d => ({ ...d, [k]: `Closed at ${j.finishTime} — ${j.totalHours} h${j.totalKm ? `, ${j.totalKm} km` : ''} (pending approval).` }));
      }
      onChanged && onChanged();
      return true;
    } catch (err) {
      addNotification(`${r.driver} ${r.date}: ${err.message}`, 'error');
      return false;
    } finally { setBusyKey(''); }
  };

  // The shift log carries WHY it says what it says — a driver reading it months later,
  // or an audit, should not have to come back and ask where the time came from.
  const noteFor = (r, e) => {
    const edited = e.finish !== r.finish ? ` Finish set by hand to ${e.finish}.` : '';
    if (r.kind === 'SCAN') return `Never signed off. Finish from the AP scanner: last on-road scan ${r.workEnd} + ${res.driveBack} min drive back (${r.delivered} delivered).${edited}${e.km ? ` ${e.km} km is their usual daily distance, not a meter reading.` : ''}`;
    if (r.kind === 'NO_SCAN') return `Never signed off and no scans that day — not on the road under N22000. Finish agreed by ${by} from ${r.driver}'s own usual day.${edited}${e.km ? ` ${e.km} km estimated from their usual distance.` : ''}`;
    return `Never signed off and no Scanner Audit loaded for ${r.date}. Finish agreed by ${by}.${edited}${e.km ? ` ${e.km} km estimated from their usual distance.` : ''}`;
  };

  // Green rows only. Amber and red are deliberately not batchable — the whole point of
  // the colour is that someone looked.
  const settleAllGreen = async () => {
    const greens = (res.rows || []).filter(r => r.confidence === 'auto' && !done[keyOf(r)]);
    if (!greens.length) return;
    setBulk({ done: 0, total: greens.length });
    let n = 0;
    for (let i = 0; i < greens.length; i++) {
      // Stop on the first failure rather than grinding through the rest. One refusal is
      // usually systemic (the database, the period, a row that changed since the review),
      // and eight identical errors tell the operator nothing the first one didn't.
      if (!await settle(greens[i])) {
        setBulk(null);
        addNotification(`Stopped after ${n} of ${greens.length} — settle the rest once that one is sorted out`, 'error');
        return;
      }
      n++;
      setBulk({ done: n, total: greens.length });
    }
    setBulk(null);
    addNotification(`${n} shift${n === 1 ? '' : 's'} settled — review them in the list below`, 'success');
  };

  const setEdit = (k, patch) => setEdits(p => ({ ...p, [k]: { ...(p[k] || {}), ...patch } }));
  const inputStyle = { background: 'var(--hp-bg)', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', padding: '7px 9px', color: 'var(--hp-text)', fontSize: '0.82rem', boxSizing: 'border-box' };
  const lblStyle = { fontSize: '0.68rem', color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', marginBottom: 5 };
  const TONE = {
    auto:    { c: 'var(--hp-green)', bg: 'rgba(52,199,89,0.07)',  br: 'rgba(52,199,89,0.32)',  label: 'Ready' },
    confirm: { c: 'var(--hp-amber)', bg: 'rgba(217,164,65,0.07)', br: 'rgba(217,164,65,0.35)', label: 'Check this' },
    manual:  { c: '#f87171',         bg: 'rgba(248,113,113,0.06)', br: 'rgba(248,113,113,0.30)', label: 'Your call' },
  };
  const fmtDate = (d) => { const dt = new Date(d + 'T00:00:00'); return isNaN(dt) ? d : dt.toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' }); };

  const visible = (res && res.rows || []).filter(r => show === 'all' || r.confidence === show);
  const greenLeft = (res && res.rows || []).filter(r => r.confidence === 'auto' && !done[keyOf(r)]).length;

  return (
    <div style={{ marginBottom: 24, background: 'var(--hp-surface)', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius-lg)', overflow: 'hidden' }}>
      <button onClick={() => setOpen(o => !o)} style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 18px', background: 'none', border: 'none', cursor: 'pointer' }}>
        <span style={{ fontSize: '0.82rem', fontWeight: 700, color: 'var(--hp-text)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Close out a period's unfinished shifts</span>
        {open ? <ChevronUp size={16} color="var(--hp-dim)" /> : <ChevronDown size={16} color="var(--hp-dim)" />}
      </button>
      {open && (
        <div style={{ padding: '0 18px 18px' }}>
          <p style={{ fontSize: '0.78rem', color: 'var(--hp-dim)', margin: '0 0 14px', lineHeight: 1.5 }}>
            Pick a pay period and review every shift left open in it. For each one the engine checks whether the driver closed the portal that day, then what the Scanner Audit shows, and proposes a finish time with the evidence beside it. <strong style={{ color: 'var(--hp-text)' }}>Green</strong> is backed by a full day of scans and can go through together. <strong style={{ color: 'var(--hp-text)' }}>Amber</strong> has scans but something is off — the reason is written out. <strong style={{ color: 'var(--hp-text)' }}>Red</strong> has no scan evidence at all and is your call. Everything written lands as PENDING in the list below.
          </p>

          {/* Period picker */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(170px, 100%), 1fr))', gap: 12, marginBottom: 14 }}>
            <div style={{ gridColumn: 'span 2', minWidth: 0 }}>
              <label style={lblStyle}>Pay period</label>
              <select value={periodIdx} onChange={e => { const i = Number(e.target.value); setPeriodIdx(i); const p = grid[i]; if (p) { setFrom(p.startISO); setTo(p.endISO); } }} style={{ ...inputStyle, width: '100%' }}>
                {grid.map((p, i) => <option key={p.endISO} value={i}>{p.label}{p.current ? ' — current' : ''}</option>)}
              </select>
            </div>
            <div><label style={lblStyle}>From</label><input type="date" value={from} onChange={e => setFrom(e.target.value)} style={{ ...inputStyle, width: '100%' }} /></div>
            <div><label style={lblStyle}>To</label><input type="date" value={to} onChange={e => setTo(e.target.value)} style={{ ...inputStyle, width: '100%' }} /></div>
            <div>
              <label style={lblStyle}>Drive back (min)</label>
              <input type="number" min="0" max="120" value={driveBack} onChange={e => setDriveBack(e.target.value === '' ? '' : Number(e.target.value))} style={{ ...inputStyle, width: '100%' }} />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', marginBottom: 16 }}>
            <button onClick={review} disabled={loading || !from || !to}
              style={{ padding: '10px 20px', background: 'var(--hp-gold)', border: 'none', borderRadius: 'var(--radius)', color: 'var(--hp-black)', fontWeight: 800, fontSize: '0.85rem', cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.5 : 1 }}>
              {loading ? 'Reviewing…' : 'Review unfinished shifts'}
            </button>
            {res && greenLeft > 0 && (
              <button onClick={settleAllGreen} disabled={!!bulk}
                style={{ padding: '10px 18px', background: 'none', border: '1px solid var(--hp-green)', borderRadius: 'var(--radius)', color: 'var(--hp-green)', fontWeight: 700, fontSize: '0.82rem', cursor: bulk ? 'not-allowed' : 'pointer' }}>
                {bulk ? `Settling ${bulk.done}/${bulk.total}…` : `Settle all ${greenLeft} green`}
              </button>
            )}
          </div>

          {res && (
            <>
              {/* What the period holds */}
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
                {[['all', `${res.counts.total} open`], ['auto', `${res.counts.auto} ready`], ['confirm', `${res.counts.confirm} to check`], ['manual', `${res.counts.manual} your call`]].map(([k, label]) => (
                  <button key={k} onClick={() => setShow(k)}
                    style={{ padding: '5px 12px', borderRadius: 999, fontSize: '0.76rem', fontWeight: 700, cursor: 'pointer',
                      background: show === k ? 'var(--hp-gold)' : 'none', color: show === k ? 'var(--hp-black)' : (k === 'all' ? 'var(--hp-dim)' : TONE[k].c),
                      border: `1px solid ${show === k ? 'var(--hp-gold)' : (k === 'all' ? 'var(--hp-border)' : TONE[k].br)}` }}>
                    {label}
                  </button>
                ))}
              </div>

              {/* Drivers out right now — listed so the count reads as complete, never offered */}
              {res.onRoad.length > 0 && (
                <div style={{ marginBottom: 12, padding: '9px 12px', background: 'var(--hp-bg)', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', fontSize: '0.76rem', color: 'var(--hp-dim)' }}>
                  {res.onRoad.length} session{res.onRoad.length === 1 ? '' : 's'} dated today or later — {res.onRoad.map(o => o.driver).join(', ')} — left alone; they may still be out working.
                </div>
              )}

              {/* The money for this window may already have gone out. Not a block — the
                  backlog is exactly what we are here to clear — but say it out loud. */}
              {(res.settled.rctis > 0 || (res.settled.paidThrough && res.settled.paidThrough >= res.from)) && (
                <div style={{ marginBottom: 14, padding: '11px 13px', background: 'rgba(217,164,65,0.08)', border: '1px solid rgba(217,164,65,0.35)', borderRadius: 'var(--radius)', fontSize: '0.78rem', color: 'var(--hp-text)', lineHeight: 1.5 }}>
                  <strong style={{ color: 'var(--hp-gold)' }}>This period has already been billed.</strong>{' '}
                  {res.settled.rctis > 0 && <>{res.settled.rctis} RCTI{res.settled.rctis === 1 ? '' : 's'} are archived against {res.settled.periods.join(', ')}. </>}
                  {res.settled.paidThrough && res.settled.paidThrough >= res.from && <>Wages are paid through {res.settled.paidThrough}. </>}
                  Hours added now move the CTTCD minimum floor and the levy behind it, but they will not change an invoice that has gone out. Add them because the day was worked and should be on record — then re-check the driver's pricing if it matters.
                </div>
              )}

              {visible.length === 0 && <div style={{ fontSize: '0.82rem', color: 'var(--hp-dim)', padding: '12px 0' }}>Nothing in this group.</div>}

              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {visible.map(r => {
                  const k = keyOf(r); const e = edits[k] || {}; const tone = TONE[r.confidence];
                  const settled = done[k];
                  return (
                    <div key={k} style={{ background: settled ? 'var(--hp-bg)' : tone.bg, border: `1px solid ${settled ? 'var(--hp-border)' : tone.br}`, borderRadius: 'var(--radius)', padding: '12px 14px', opacity: settled ? 0.65 : 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 6 }}>
                        <span style={{ padding: '2px 9px', borderRadius: 999, background: tone.c, color: 'var(--hp-black)', fontSize: '0.66rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{tone.label}</span>
                        <strong style={{ fontSize: '0.86rem', color: 'var(--hp-text)' }}>{fmtDate(r.date)} — {r.driver}</strong>
                        {r.name && r.name !== r.driver && <span style={{ fontSize: '0.78rem', color: 'var(--hp-dim)' }}>{r.name}</span>}
                        <span style={{ fontSize: '0.78rem', color: 'var(--hp-dim)' }}>· clocked on {r.start}</span>
                      </div>
                      <div style={{ fontSize: '0.8rem', color: 'var(--hp-text)', marginBottom: 4 }}>{r.headline}</div>
                      {r.evidence && <div style={{ fontSize: '0.76rem', color: 'var(--hp-dim)', marginBottom: 6 }}>{r.evidence}</div>}
                      {r.notes.map((n, i) => (
                        <div key={i} style={{ fontSize: '0.76rem', color: 'var(--hp-text)', marginBottom: 4, paddingLeft: 12, borderLeft: `2px solid ${tone.br}`, lineHeight: 1.45 }}>{n}</div>
                      ))}

                      {settled ? (
                        <div style={{ marginTop: 8, fontSize: '0.78rem', color: 'var(--hp-green)', fontWeight: 700 }}>✓ {settled}</div>
                      ) : (
                        <div style={{ marginTop: 10, display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                          {r.action === 'close' && (
                            <>
                              <div style={{ width: 105 }}><label style={lblStyle}>Start</label>
                                <input type="time" value={e.start || ''} onChange={ev => setEdit(k, { start: ev.target.value })} style={{ ...inputStyle, width: '100%' }} /></div>
                              <div style={{ width: 105 }}><label style={lblStyle}>Finish</label>
                                <input type="time" value={e.finish || ''} onChange={ev => setEdit(k, { finish: ev.target.value })} style={{ ...inputStyle, width: '100%' }} /></div>
                              <div style={{ width: 92 }}><label style={lblStyle}>KM</label>
                                <input type="number" placeholder="—" value={e.km || ''} onChange={ev => setEdit(k, { km: ev.target.value })} style={{ ...inputStyle, width: '100%' }} /></div>
                              {r.altFinish && (
                                <button onClick={() => setEdit(k, { finish: r.altFinish })}
                                  style={{ padding: '7px 11px', background: 'none', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', color: 'var(--hp-dim)', fontSize: '0.74rem', cursor: 'pointer' }}>
                                  Use {r.altFinish}
                                </button>
                              )}
                            </>
                          )}
                          <button onClick={() => settle(r)} disabled={busyKey === k || !!bulk}
                            style={{ padding: '8px 16px', background: tone.c, border: 'none', borderRadius: 'var(--radius)', color: 'var(--hp-black)', fontWeight: 800, fontSize: '0.8rem', cursor: (busyKey === k || bulk) ? 'not-allowed' : 'pointer', opacity: (busyKey === k || bulk) ? 0.5 : 1 }}>
                            {busyKey === k ? 'Saving…' : r.action === 'clearStale' ? 'Clear session' : 'Close shift'}
                          </button>
                          {r.kmBasis && r.action === 'close' && <span style={{ fontSize: '0.72rem', color: 'var(--hp-dim)', alignSelf: 'center' }}>km: {r.kmBasis}</span>}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// Admin tool: repair missed shifts from the AP Scanner Audit file. Upload the day's
// .xlsx — it's parsed SERVER-SIDE (scan-audit-sb) down to one summary row per driver
// (first/last scan, delivered count); addresses and signatory names are never stored.
// Drivers who scanned parcels but have no shift log for the day are flagged, with
// start/finish prefilled from their first/last scan (editable). Repairs go through
// the normal adminAddShift / adminFinishShift paths and land as PENDING approvals,
// noted as scan-derived so the driver can see where the times came from.
function ScanRepair({ addNotification, by, onRepaired }) {
  const [open, setOpen]           = useState(false);
  const [date, setDate]           = useState('');
  const [rows, setRows]           = useState(null);
  const [loading, setLoading]     = useState(false);
  const [uploading, setUploading] = useState(false);
  const [edits, setEdits]         = useState({}); // driver -> { start, finish }
  const [busyDriver, setBusyDriver] = useState('');
  const [dragOver, setDragOver]   = useState(false);
  const fileRef = useRef(null);

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (uploading) return;
    const files = [...((e.dataTransfer && e.dataTransfer.files) || [])].filter(f => /\.xlsx?$/i.test(f.name));
    if (!files.length) { addNotification('Drop a Scanner Audit .xlsx file', 'error'); return; }
    upload(files);
  };

  const load = useCallback(async (d) => {
    if (!d) return;
    setLoading(true);
    try {
      const res = await fetch('/.netlify/functions/scan-audit-sb', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'getScanRepairCandidates', date: d }),
      });
      const j = await res.json();
      if (!j.success) throw new Error(j.error || 'Load failed');
      setRows(j.rows);
      const seed = {};
      // Start defaults to the app login time when the driver logged in correctly
      // (stuck session), else the first scan; finish defaults to the last scan.
      // Both are editable — the operator amends whatever the automation got wrong.
      j.rows.filter(r => r.needsRepair).forEach(r => { seed[r.driver] = { start: r.appLogin || r.firstScan, finish: r.lastScan }; });
      setEdits(seed);
    } catch (e) { addNotification('Scan data: ' + e.message, 'error'); setRows(null); }
    finally { setLoading(false); }
  }, [addNotification]);

  const upload = async (files) => {
    if (!files || !files.length) return;
    setUploading(true);
    const dates = new Set();
    try {
      for (const file of files) {
        const b64 = await new Promise((resolve, reject) => {
          const fr = new FileReader();
          fr.onload = () => resolve(String(fr.result).split(',')[1] || '');
          fr.onerror = reject;
          fr.readAsDataURL(file);
        });
        const res = await fetch('/.netlify/functions/scan-audit-sb', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: 'ingestScanAudit', fileName: file.name, fileBase64: b64 }),
        });
        const j = await res.json();
        if (!j.success) throw new Error(`${file.name}: ${j.error || 'ingest failed'}`);
        (j.dates || []).forEach(dt => dates.add(dt));
      }
      const sorted = [...dates].sort();
      addNotification(`Scan data loaded for ${sorted.join(', ')}`, 'success');
      if (sorted.length) { setDate(sorted[0]); await load(sorted[0]); }
    } catch (e) { addNotification('Upload failed: ' + e.message, 'error'); }
    finally { setUploading(false); }
  };

  const repair = async (r) => {
    const e = edits[r.driver] || {};
    const startVal  = e.start  || r.appLogin || r.firstScan;
    const finishVal = e.finish || r.lastScan;
    const scanNote = r.stuckSession
      ? `Start = app login ${startVal}; finish from AP scanner last scan ${r.lastScan} (${r.delivered} delivered)`
      : `Times from AP scanner data (first scan ${r.firstScan}, last scan ${r.lastScan}, ${r.delivered} delivered)`;
    setBusyDriver(r.driver);
    try {
      const body = r.stuckSession
        ? { action: 'adminFinishShift', driver: r.driver, date, startTime: startVal, finishTime: finishVal, note: scanNote, by: by || 'ADMIN' }
        : { action: 'adminAddShift', driver: r.driver, date, start: startVal, finish: finishVal, note: scanNote, by: by || 'ADMIN' };
      const res = await fetch('/.netlify/functions/appsscript', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
      });
      const j = await res.json();
      if (!j.success) throw new Error(j.error || 'Repair failed');
      addNotification(`${r.driver} ${date} repaired — ${j.totalHours} hrs (pending approval)`, 'success');
      await load(date);
      onRepaired && onRepaired();
    } catch (err) { addNotification(`Could not repair ${r.driver}: ` + err.message, 'error'); }
    finally { setBusyDriver(''); }
  };

  const candidates = (rows || []).filter(r => r.needsRepair);
  const ok = (rows || []).filter(r => !r.needsRepair);
  const inputStyle = { background: 'var(--hp-bg)', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', padding: '6px 8px', color: 'var(--hp-text)', fontSize: '0.8rem', boxSizing: 'border-box' };
  const lblStyle = { fontSize: '0.7rem', color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', marginBottom: 6 };

  return (
    <div style={{ marginBottom: 24, background: 'var(--hp-surface)', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius-lg)', overflow: 'hidden' }}>
      <button onClick={() => setOpen(o => !o)} style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 18px', background: 'none', border: 'none', cursor: 'pointer' }}>
        <span style={{ fontSize: '0.82rem', fontWeight: 700, color: 'var(--hp-text)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Repair missed shifts from scanner audit</span>
        {open ? <ChevronUp size={16} color="var(--hp-dim)" /> : <ChevronDown size={16} color="var(--hp-dim)" />}
      </button>
      {open && (
        <div style={{ padding: '0 18px 18px' }}>
          <p style={{ fontSize: '0.78rem', color: 'var(--hp-dim)', margin: '0 0 14px' }}>
            Drop the day's Scanner Audit .xlsx — it's summarised on the server (first/last scan per driver; addresses and signatures are never stored). Drivers who scanned parcels but never logged a shift are listed with times prefilled from their scans; adjust if needed and repair. Repaired shifts are marked as scan-derived and land in the approvals list above.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(200px, 100%), 1fr))', gap: 12, marginBottom: 14 }}>
            <div>
              <label style={lblStyle}>Scanner Audit file(s)</label>
              <div
                onClick={() => { if (!uploading && fileRef.current) fileRef.current.click(); }}
                onDragOver={e => { e.preventDefault(); if (!uploading) setDragOver(true); }}
                onDragLeave={e => { e.preventDefault(); setDragOver(false); }}
                onDrop={onDrop}
                style={{
                  ...inputStyle, width: '100%', padding: '14px 10px', textAlign: 'center',
                  lineHeight: 1.4, cursor: uploading ? 'default' : 'pointer',
                  borderStyle: 'dashed', borderColor: dragOver ? 'var(--hp-gold)' : 'var(--hp-border)',
                  background: dragOver ? 'rgba(217,164,65,0.10)' : 'var(--hp-bg)',
                  color: 'var(--hp-dim)', transition: 'background 0.12s, border-color 0.12s',
                }}>
                {uploading ? 'Uploading…' : dragOver ? 'Drop to upload' : (
                  <>Drag Scanner Audit .xlsx here<br />
                  <span style={{ fontSize: '0.72rem' }}>or click to browse (multiple days OK)</span></>
                )}
              </div>
              <input ref={fileRef} type="file" accept=".xlsx,.xls" multiple disabled={uploading}
                onChange={e => { upload([...e.target.files]); e.target.value = ''; }}
                style={{ display: 'none' }} />
            </div>
            <div>
              <label style={lblStyle}>Scan date</label>
              <input type="date" value={date} onChange={e => { setDate(e.target.value); load(e.target.value); }} style={{ ...inputStyle, width: '100%', padding: '8px 10px' }} />
            </div>
          </div>
          {(uploading || loading) && <div style={{ fontSize: '0.82rem', color: 'var(--hp-dim)' }}>{uploading ? 'Uploading + summarising…' : 'Checking shift logs…'}</div>}
          {!uploading && !loading && rows && (
            <>
              {candidates.length === 0 ? (
                <div style={{ fontSize: '0.85rem', color: 'var(--hp-green)', fontWeight: 600 }}>✓ Every driver who scanned on {date} has a shift log.</div>
              ) : (
                <div style={{ marginBottom: 12, padding: '12px 14px', background: 'rgba(217,164,65,0.08)', border: '1px solid rgba(217,164,65,0.35)', borderRadius: 'var(--radius)' }}>
                  <div style={{ fontSize: '0.74rem', fontWeight: 700, color: 'var(--hp-gold)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>
                    ⚠ {candidates.length} driver{candidates.length === 1 ? '' : 's'} scanned parcels on {date} but ha{candidates.length === 1 ? 's' : 've'} no shift log
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {candidates.map(r => (
                      <div key={r.driver} style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 10, fontSize: '0.8rem', color: 'var(--hp-text)' }}>
                        <span style={{ minWidth: 190 }}>
                          <strong>{r.driver}</strong> · scans {r.firstScan}–{r.lastScan} · {r.delivered} delivered
                          {r.stuckSession
                            ? <span style={{ color: 'var(--hp-amber, #f59e0b)' }}> · app login {r.appLogin || '—'} · stuck session (will be closed)</span>
                            : ''}
                        </span>
                        <label style={{ fontSize: '0.72rem', color: 'var(--hp-dim)' }}>start{' '}
                          <input type="time" value={(edits[r.driver] || {}).start || ''} style={inputStyle}
                            onChange={e => setEdits(x => ({ ...x, [r.driver]: { ...x[r.driver], start: e.target.value } }))} />
                        </label>
                        <label style={{ fontSize: '0.72rem', color: 'var(--hp-dim)' }}>finish{' '}
                          <input type="time" value={(edits[r.driver] || {}).finish || ''} style={inputStyle}
                            onChange={e => setEdits(x => ({ ...x, [r.driver]: { ...x[r.driver], finish: e.target.value } }))} />
                        </label>
                        <button onClick={() => repair(r)} disabled={busyDriver === r.driver}
                          style={{ padding: '5px 14px', background: 'none', border: '1px solid var(--hp-gold)', borderRadius: 'var(--radius)', color: 'var(--hp-gold)', fontWeight: 700, fontSize: '0.74rem', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                          {busyDriver === r.driver ? 'Repairing…' : 'Repair →'}
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {ok.length > 0 && (
                <div style={{ fontSize: '0.74rem', color: 'var(--hp-dim)' }}>
                  {ok.length} driver{ok.length === 1 ? '' : 's'} with scans already logged: {ok.map(r => r.driver).join(', ')}
                </div>
              )}
            </>
          )}
          {!uploading && !loading && !rows && date && (
            <div style={{ fontSize: '0.82rem', color: 'var(--hp-dim)' }}>No scan data for {date} yet — upload that day's audit file.</div>
          )}
        </div>
      )}
    </div>
  );
}

// Portal-logoff vs last-scan analysis over a period, with parcels-per-hour.
// Classification is done server-side (scan-audit-sb getLogoffVariance):
// BAD_RECORD (impossible portal times) · REVIEW (logoff > threshold after last
// scan — assess each) · AFTER_LOGOFF (kept scanning after logoff — accepted,
// e.g. finishing the scan process at home) · ADMIN (BOUVENG logins: genuine
// office time, isolated so delivery rate stays honest) · STRAY_ONLY (last scan
// was an outlier; work_end used instead — e.g. parcel completed hours later).
function LogoffVariance({ addNotification }) {
  const todayISO = () => new Date().toISOString().slice(0, 10);
  const daysAgoISO = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return d.toISOString().slice(0, 10); };
  const [open, setOpen]         = useState(false);
  const [from, setFrom]         = useState(daysAgoISO(14));
  const [to, setTo]             = useState(todayISO());
  const [threshold, setThreshold] = useState(45);
  const [busy, setBusy]         = useState(false);
  const [result, setResult]     = useState(null);
  // Inline edit of a flagged row's portal times: submittedAt of the row being
  // edited, its draft start/finish, and save-in-flight state.
  const [editKey, setEditKey]   = useState(null);
  const [draft, setDraft]       = useState({ start: '', finish: '' });
  const [saving, setSaving]     = useState(false);

  const saveRow = async (r) => {
    setSaving(true);
    try {
      const res = await fetch('/.netlify/functions/appsscript', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'editShiftTimes', submittedAt: r.submittedAt, startTime: draft.start, finishTime: draft.finish, by: 'ADMIN' }),
      });
      const j = await res.json();
      if (!j.success) throw new Error(j.error || 'Edit failed');
      addNotification(`${r.driver} ${r.date} updated — ${j.totalHours ?? '?'} hrs`, 'success');
      setEditKey(null);
      await run();
    } catch (e) { addNotification('Could not update: ' + e.message, 'error'); }
    finally { setSaving(false); }
  };

  const run = async () => {
    setBusy(true);
    try {
      const res = await fetch('/.netlify/functions/scan-audit-sb', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'getLogoffVariance', from, to, thresholdMins: threshold }),
      });
      const j = await res.json();
      if (!j.success) throw new Error(j.error || 'Analysis failed');
      setResult(j);
    } catch (e) { addNotification('Logoff analysis: ' + e.message, 'error'); setResult(null); }
    finally { setBusy(false); }
  };

  const fmtVar = (m) => m == null ? '—' : `${m > 0 ? '+' : '−'}${Math.floor(Math.abs(m) / 60)}h${String(Math.abs(m) % 60).padStart(2, '0')}`;
  const inputStyle = { background: 'var(--hp-bg)', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', padding: '6px 8px', color: 'var(--hp-text)', fontSize: '0.8rem', boxSizing: 'border-box' };
  const lblStyle = { fontSize: '0.7rem', color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', marginBottom: 6 };
  const chip = (label, value, colour) => (
    <div style={{ padding: '8px 12px', background: 'var(--hp-bg)', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', minWidth: 110 }}>
      <div style={{ fontSize: '0.66rem', color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</div>
      <div style={{ fontSize: '1.05rem', fontWeight: 700, color: colour || 'var(--hp-text)' }}>{value}</div>
    </div>
  );
  const SECTIONS = [
    { kind: 'BAD_RECORD',  title: 'Bad portal records — fix these times',            colour: 'var(--hp-red, #e5484d)',  border: 'rgba(229,72,77,0.35)',  bg: 'rgba(229,72,77,0.07)' },
    { kind: 'REVIEW',      title: 'Logoff well after last scan — assess each',       colour: 'var(--hp-gold)',          border: 'rgba(217,164,65,0.35)', bg: 'rgba(217,164,65,0.07)' },
    { kind: 'NO_LOG',      title: 'Scanned but no shift log',                        colour: 'var(--hp-amber, #f59e0b)', border: 'rgba(245,158,11,0.35)', bg: 'rgba(245,158,11,0.07)' },
    { kind: 'AFTER_LOGOFF', title: 'Scanned after logoff — accepted (info only)',    colour: 'var(--hp-dim)',           border: 'var(--hp-border)',      bg: 'transparent' },
    { kind: 'ADMIN',       title: 'Admin logins — office time, isolated from delivery rate', colour: 'var(--hp-dim)',  border: 'var(--hp-border)',      bg: 'transparent' },
    { kind: 'STRAY_ONLY',  title: 'Stray final scan (variance OK once discounted)',  colour: 'var(--hp-dim)',           border: 'var(--hp-border)',      bg: 'transparent' },
  ];

  return (
    <div style={{ marginBottom: 24, background: 'var(--hp-surface)', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius-lg)', overflow: 'hidden' }}>
      <button onClick={() => setOpen(o => !o)} style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 18px', background: 'none', border: 'none', cursor: 'pointer' }}>
        <span style={{ fontSize: '0.82rem', fontWeight: 700, color: 'var(--hp-text)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Logoff vs last scan · parcels per hour</span>
        {open ? <ChevronUp size={16} color="var(--hp-dim)" /> : <ChevronDown size={16} color="var(--hp-dim)" />}
      </button>
      {open && (
        <div style={{ padding: '0 18px 18px' }}>
          <p style={{ fontSize: '0.78rem', color: 'var(--hp-dim)', margin: '0 0 14px' }}>
            Compares each driver's portal logoff to their last scan across a period (needs the scanner audits uploaded above). A stray final scan — a parcel completed hours after the run — is discounted automatically. Admin logins are kept in contract totals but isolated from the delivery rate.
          </p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-end', marginBottom: 14 }}>
            <div><label style={lblStyle}>From</label><input type="date" value={from} onChange={e => setFrom(e.target.value)} style={inputStyle} /></div>
            <div><label style={lblStyle}>To</label><input type="date" value={to} onChange={e => setTo(e.target.value)} style={inputStyle} /></div>
            <div><label style={lblStyle}>Alert over (mins)</label><input type="number" min="5" max="480" value={threshold} onChange={e => setThreshold(parseInt(e.target.value, 10) || 45)} style={{ ...inputStyle, width: 90 }} /></div>
            <button onClick={run} disabled={busy} style={{ padding: '8px 18px', background: 'var(--hp-gold)', border: 'none', borderRadius: 'var(--radius)', color: '#1a1408', fontWeight: 700, fontSize: '0.78rem', cursor: 'pointer' }}>
              {busy ? 'Analysing…' : 'Run analysis'}
            </button>
          </div>
          {result && (
            <>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 16 }}>
                {chip('Driver-days', result.summary.driverDays)}
                {chip('Delivered', result.summary.delivered.toLocaleString())}
                {chip('Delivery hrs', result.summary.deliveryHours.toLocaleString())}
                {chip('Admin hrs', result.summary.adminHours.toLocaleString())}
                {chip('Delivery parcels/hr', result.summary.deliveryRate, 'var(--hp-green)')}
                {chip('Contract parcels/hr (incl admin)', result.summary.contractRate, 'var(--hp-gold)')}
              </div>
              {result.rows.length === 0 && (
                <div style={{ fontSize: '0.85rem', color: 'var(--hp-green)', fontWeight: 600 }}>✓ No variances over {result.threshold} min in this period.</div>
              )}
              {SECTIONS.map(sec => {
                const list = result.rows.filter(r => r.kind === sec.kind);
                if (!list.length) return null;
                return (
                  <div key={sec.kind} style={{ marginBottom: 12, padding: '12px 14px', background: sec.bg, border: `1px solid ${sec.border}`, borderRadius: 'var(--radius)' }}>
                    <div style={{ fontSize: '0.72rem', fontWeight: 700, color: sec.colour, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>
                      {sec.title} ({list.length})
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                      {list.map((r, i) => {
                        const key = r.date + '|' + r.driver;
                        const editing = editKey === key;
                        return (
                          <div key={key + i} style={{ display: 'flex', flexWrap: 'wrap', gap: 10, fontSize: '0.78rem', color: 'var(--hp-text)', alignItems: 'center' }}>
                            <span style={{ color: 'var(--hp-dim)', minWidth: 78 }}>{r.date}</span>
                            <strong style={{ minWidth: 105 }}>{r.driver}</strong>
                            <span>scans {r.firstScan || '?'}–{r.effLast || '?'}{r.strayLast ? <span style={{ color: 'var(--hp-amber, #f59e0b)' }}> (stray {r.lastScan})</span> : ''}</span>
                            {!editing && <span style={{ color: 'var(--hp-dim)' }}>portal {r.start || '—'}–{r.finish || '—'}</span>}
                            <span style={{ fontWeight: 700, color: sec.colour }}>{fmtVar(r.varianceMins)}</span>
                            <span style={{ color: 'var(--hp-dim)' }}>{r.delivered} del</span>
                            {r.submittedAt && !editing && (
                              <button onClick={() => { setEditKey(key); setDraft({ start: r.start || '', finish: r.finish || '' }); }}
                                style={{ padding: '3px 10px', background: 'none', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', color: 'var(--hp-dim)', fontSize: '0.7rem', cursor: 'pointer' }}>
                                ✎ Edit
                              </button>
                            )}
                            {editing && (
                              <span style={{ display: 'inline-flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                                <label style={{ fontSize: '0.7rem', color: 'var(--hp-dim)' }}>start{' '}
                                  <input type="time" value={draft.start} onChange={e => setDraft(d => ({ ...d, start: e.target.value }))} style={inputStyle} />
                                </label>
                                <label style={{ fontSize: '0.7rem', color: 'var(--hp-dim)' }}>finish{' '}
                                  <input type="time" value={draft.finish} onChange={e => setDraft(d => ({ ...d, finish: e.target.value }))} style={inputStyle} />
                                </label>
                                <button onClick={() => saveRow(r)} disabled={saving}
                                  style={{ padding: '4px 12px', background: 'var(--hp-gold)', border: 'none', borderRadius: 'var(--radius)', color: '#1a1408', fontWeight: 700, fontSize: '0.72rem', cursor: 'pointer' }}>
                                  {saving ? 'Saving…' : 'Save'}
                                </button>
                                <button onClick={() => setEditKey(null)} disabled={saving}
                                  style={{ padding: '4px 10px', background: 'none', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', color: 'var(--hp-dim)', fontSize: '0.72rem', cursor: 'pointer' }}>
                                  Cancel
                                </button>
                              </span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default function ShiftApprovalsPage() {
  const { addNotification, personnel, currentUser } = useApp();
  const [rows, setRows] = useState([]);
  const [photos, setPhotos] = useState({});
  const [loading, setLoading] = useState(false);
  const [days, setDays] = useState(28);
  const [filterStatus, setFilterStatus] = useState('ALL');
  // ?driver= deep link from the global search palette pre-fills the driver filter.
  const [searchParams] = useSearchParams();
  const driverParam = searchParams.get('driver') || '';
  const [filterDriver, setFilterDriver] = useState(driverParam);
  useEffect(() => { if (driverParam) setFilterDriver(driverParam); }, [driverParam]);
  // Explicit pay-period range (inclusive, YYYY-MM-DD). When set it both filters
  // the list and widens the fetch window so an older period is actually loaded.
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate]     = useState('');
  const [expandedRow, setExpandedRow] = useState(null);
  const [rejectModal, setRejectModal] = useState({ open: false, row: null, reason: '' });
  const [actionLoading, setActionLoading] = useState({});

  useEffect(() => {
    const logins = [...new Set((rows || []).map(x => (x.driver || '').toUpperCase()).filter(Boolean))];
    if (logins.length) fetchDriverPhotos(logins).then(p => setPhotos(prev => ({ ...p, ...prev })));
  }, [rows]);
  const [bulkBusy, setBulkBusy] = useState(null); // { done, total } while Approve All runs
  // Inline edit of a submitted shift's finish time / odometer
  const [editRow, setEditRow]       = useState(null); // submittedAt being edited
  const [editStart, setEditStart]   = useState('');
  const [editFinish, setEditFinish] = useState('');
  const [editBreakStart, setEditBreakStart] = useState('');
  const [editBreakEnd, setEditBreakEnd]     = useState('');
  const [editOdo, setEditOdo]       = useState('');
  const [editBusy, setEditBusy]     = useState(false);

  async function saveEdit(row) {
    setEditBusy(true);
    try {
      const res = await fetch('/.netlify/functions/appsscript', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'editShiftTimes', submittedAt: row.submittedAt, startTime: editStart, finishTime: editFinish, breakStart: editBreakStart, breakEnd: editBreakEnd, odoEnd: editOdo !== '' ? Number(editOdo) : '', by: (currentUser?.login || 'ADMIN').toUpperCase() }),
      });
      const d = await res.json();
      if (!d.success) throw new Error(d.error || 'Edit failed');
      setRows(prev => prev.map(r => r.submittedAt === row.submittedAt
        ? { ...r, shiftStart: d.shiftStart ?? r.shiftStart, finishTime: d.finishTime ?? r.finishTime, breakStart: d.breakStart ?? r.breakStart, breakEnd: d.breakEnd ?? r.breakEnd, totalHours: d.totalHours ?? r.totalHours, odoEnd: d.odoEnd ?? r.odoEnd, totalKm: d.totalKm ?? r.totalKm }
        : r));
      addNotification('Shift updated', 'success');
      setEditRow(null);
    } catch (e) { addNotification('Could not update: ' + e.message, 'error'); }
    finally { setEditBusy(false); }
  }

  // ── Depot QR panel ────────────────────────────────────────────────────────
  const [qrOpen, setQrOpen]     = useState(false);
  const [depots, setDepots]     = useState([]);
  const [qrLoading, setQrLoading] = useState(false);

  // Visa-cap watch (monitoring only): which drivers on screen are visa-capped, and
  // where their combined fortnight hours (driving + night sort) sit against 48h.
  const [capWatch, setCapWatch] = useState({});
  const watchCaps = useCallback(async (rws) => {
    try {
      const logins = [...new Set((rws || []).map((r) => (r.driver || '').toUpperCase()).filter(Boolean))];
      if (!logins.length) { setCapWatch({}); return; }
      const res = await fetch('/.netlify/functions/driver-dms-sb', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'capWatch', logins }),
      });
      const j = await res.json();
      setCapWatch((j && j.capWatch) || {});
    } catch { /* best-effort — approvals work without the watch */ }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch enough history to cover BOTH the preset window and any explicit
      // From date — otherwise an older pay period would be filtered against data
      // that was never loaded, and silently come back empty.
      let fetchDays = days;
      if (fromDate) {
        const span = Math.ceil((Date.now() - new Date(`${fromDate}T00:00:00`).getTime()) / 86400000) + 2;
        fetchDays = days > 0 ? Math.max(days, span) : days; // days===0 already means "all records"
      }
      const qs  = fetchDays > 0 ? `?days=${fetchDays}` : '';
      const res = await fetch(`/.netlify/functions/driver-logs${qs}`);
      const data = await res.json();
      if (data.success) { setRows(data.rows); watchCaps(data.rows); }
      else addNotification('Could not load shift logs', 'error');
    } catch {
      addNotification('Could not load shift logs', 'error');
    } finally {
      setLoading(false);
    }
  }, [days, fromDate, addNotification, watchCaps]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!qrOpen || depots.length > 0) return;
    setQrLoading(true);
    fetch('/.netlify/functions/depot-qr-urls')
      .then(r => r.json())
      .then(d => { setDepots(d.depots || []); setQrLoading(false); })
      .catch(() => setQrLoading(false));
  }, [qrOpen, depots.length]);

  // ── Filtered view ─────────────────────────────────────────────────────────
  const filtered = rows.filter(r => {
    if (filterStatus !== 'ALL' && r.status !== filterStatus) return false;
    if (filterDriver && !r.name.toLowerCase().includes(filterDriver.toLowerCase()) &&
        !r.driver.toLowerCase().includes(filterDriver.toLowerCase())) return false;
    if (fromDate && r.date < fromDate) return false;
    if (toDate && r.date > toDate) return false;
    return true;
  });

  // Hours across the current view — the pay-period tally. Compares dates as
  // plain ISO strings, so it lines up exactly with the From/To range above.
  //
  // Counts only what would actually be paid, which is NOT simply every visible row:
  // an amended shift leaves its SUPERSEDED original on screen (deliberately — it's the
  // audit trail), and summing both billed the day twice. REJECTED rows are never paid
  // either. Beyond status, a day can also carry two live rows when a shift got submitted
  // twice, so this collapses to one row per driver+date — the latest submission — which
  // is the same rule getPortalShiftTimes uses to build the wage run. Keep the two in step.
  const payableRows = React.useMemo(() => {
    const best = new Map();
    for (const r of filtered) {
      const st = (r.status || '').toString().toUpperCase();
      if (st === 'SUPERSEDED' || st === 'REJECTED') continue;
      const key = `${(r.driver || '').toUpperCase()}|${r.date}`;
      const prev = best.get(key);
      if (!prev || (r.submittedAt || '') > (prev.submittedAt || '')) best.set(key, r);
    }
    return [...best.values()];
  }, [filtered]);
  const totalHours = payableRows.reduce((s, r) => s + (Number(r.totalHours) || 0), 0);

  // Days where two live rows survived for the same driver — a double submission that no
  // amendment superseded. The tally above only pays one, so the total stays honest, but
  // the pair is a real data fault someone has to resolve; surface it rather than hide it.
  const dupDays = React.useMemo(() => {
    const seen = new Map();
    for (const r of filtered) {
      const st = (r.status || '').toString().toUpperCase();
      if (st === 'SUPERSEDED' || st === 'REJECTED') continue;
      const key = `${(r.driver || '').toUpperCase()}|${r.date}`;
      seen.set(key, (seen.get(key) || 0) + 1);
    }
    return [...seen.entries()].filter(([, n]) => n > 1).map(([k]) => k);
  }, [filtered]);

  // ── Stats ─────────────────────────────────────────────────────────────────
  const today = new Date().toISOString().split('T')[0];
  const pendingCount   = rows.filter(r => r.status === 'PENDING').length;
  const approvedToday  = rows.filter(r => r.status === 'APPROVED' && r.date === today).length;
  const amendmentCount = rows.filter(r => r.type === 'AMENDMENT' && r.status === 'PENDING').length;

  // ── Actions ───────────────────────────────────────────────────────────────
  const reviewedBy = (currentUser?.login || 'ADMIN').toUpperCase();

  // Approve one shift on the server. Returns true on success; caller owns the
  // notification + row update so this can be shared by the single button and Approve All.
  async function approveOne(row) {
    const res = await fetch('/.netlify/functions/appsscript', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        action: 'approveShift',
        submittedAt: row.submittedAt,
        type: row.type,
        reviewedBy,
      }),
    });
    const data = await res.json();
    if (!data.success) throw new Error(data.error || 'Approve failed');
  }

  async function approve(row) {
    setActionLoading(a => ({ ...a, [row.submittedAt]: 'approving' }));
    try {
      await approveOne(row);
      addNotification(`${row.name} shift approved`, 'success');
      setRows(prev => prev.map(r => r.submittedAt === row.submittedAt ? { ...r, status: 'APPROVED' } : r));
    } catch (e) {
      addNotification(e.message === 'Failed to fetch' ? 'Could not connect' : e.message, 'error');
    } finally {
      setActionLoading(a => { const n = { ...a }; delete n[row.submittedAt]; return n; });
    }
  }

  // Approve every PENDING shift in the current filtered view (respects the driver
  // search, date range and status tabs). Runs a few at a time so a big backlog
  // clears quickly without hammering the Apps Script proxy.
  async function approveAll() {
    const targets = filtered.filter(r => r.status === 'PENDING');
    if (!targets.length) return;
    const amendments = targets.filter(r => r.type === 'AMENDMENT').length;
    const msg = `Approve ${targets.length} pending shift${targets.length !== 1 ? 's' : ''}` +
      (amendments ? ` (including ${amendments} amendment${amendments !== 1 ? 's' : ''})` : '') +
      `?\n\nThis approves everything currently shown in the list.`;
    if (!window.confirm(msg)) return;

    setBulkBusy({ done: 0, total: targets.length });
    let ok = 0, failed = 0;
    const CONCURRENCY = 4;
    for (let i = 0; i < targets.length; i += CONCURRENCY) {
      const batch = targets.slice(i, i + CONCURRENCY);
      await Promise.all(batch.map(async (row) => {
        try {
          await approveOne(row);
          ok++;
          setRows(prev => prev.map(r => r.submittedAt === row.submittedAt ? { ...r, status: 'APPROVED' } : r));
        } catch {
          failed++;
        } finally {
          setBulkBusy(b => (b ? { ...b, done: b.done + 1 } : b));
        }
      }));
    }
    setBulkBusy(null);
    if (failed) addNotification(`Approved ${ok}, ${failed} failed — check and retry`, failed > ok ? 'error' : 'info');
    else addNotification(`Approved ${ok} shift${ok !== 1 ? 's' : ''}`, 'success');
  }

  // Rounding to a quarter hour is BOUVENGJ's arrangement alone — every other driver
  // is paid on their captured times, so their shifts must never be snapped. The
  // restriction lives here, on the selection, rather than on the filters above: the
  // view is one mis-click from showing someone else, and that mis-click would
  // otherwise rewrite their hours.
  const ROUND_LOGIN = 'BOUVENGJ';

  // The date her wages are PAID THROUGH — the server's lock, not a guess from the
  // calendar. A date-derived cutoff closed the 15th→14th period the moment the 15th
  // arrived, which refused the shifts of the run still being reconciled. Anything on or
  // before this is settled and must not be re-priced; anything after it is open.
  // null = not loaded yet.
  const [paidThrough, setPaidThrough] = useState(null);
  const [paidBusy, setPaidBusy]       = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetch('/.netlify/functions/appsscript', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'getWagesPaidThrough', login: ROUND_LOGIN }),
    })
      .then(r => r.json())
      .then(d => { if (!cancelled && d.success) setPaidThrough(d.paidThrough || ''); })
      .catch(() => { /* leave null — rounding stays hidden rather than guessing */ });
    return () => { cancelled = true; };
  }, []);

  // Conservative while the lock is unknown: no lock, no rounding. The server re-checks
  // regardless, so erring this way only ever hides the button — it can't expose a period.
  const isSettled = useCallback(
    (r) => (!paidThrough ? true : (r.date || '') <= paidThrough),
    [paidThrough]
  );

  // Lock the run just paid. Advancing this is one-way in practice — it's what stops a
  // later click from re-pricing wages that have gone out — so it confirms with the date.
  async function markPaidThrough(date) {
    if (!date) return;
    if (!window.confirm(
      `Mark ${ROUND_LOGIN}'s wages as paid through ${date}?\n\n` +
      `Shifts on or before ${date} can no longer be rounded — that's the point: it stops a later click re-pricing wages already paid.\n\n` +
      `Only do this once the run has actually gone out.`
    )) return;
    setPaidBusy(true);
    try {
      const res = await fetch('/.netlify/functions/appsscript', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'setWagesPaidThrough', login: ROUND_LOGIN, date, by: (currentUser?.login || 'ADMIN').toUpperCase() }),
      });
      const d = await res.json();
      if (!d.success) throw new Error(d.error || 'Could not save');
      setPaidThrough(d.paidThrough);
      addNotification(`Wages locked as paid through ${d.paidThrough}`, 'success');
    } catch (e) { addNotification('Could not update: ' + e.message, 'error'); }
    finally { setPaidBusy(false); }
  }

  // Rows in the current view whose start or finish isn't already on a quarter
  // hour — the work "Round to 15" would actually do.
  const roundable = filtered.filter(r => {
    if ((r.driver || '').toUpperCase() !== ROUND_LOGIN) return false;
    if (isSettled(r)) return false;
    const rs = roundHHMMTo15(r.shiftStart), rf = roundHHMMTo15(r.finishTime);
    if (!rs && !rf) return false;
    return (rs && rs !== r.shiftStart) || (rf && rf !== r.finishTime);
  });

  // Hers, off-grid, but already paid — surfaced so a skipped shift reads as a deliberate
  // refusal rather than the button quietly missing rows.
  const settledSkipped = filtered.filter(r => {
    if ((r.driver || '').toUpperCase() !== ROUND_LOGIN) return false;
    if (!isSettled(r)) return false;
    const rs = roundHHMMTo15(r.shiftStart), rf = roundHHMMTo15(r.finishTime);
    if (!rs && !rf) return false;
    return (rs && rs !== r.shiftStart) || (rf && rf !== r.finishTime);
  });

  // Snap BOUVENGJ's shifts in the current view to the nearest quarter hour. The date
  // range above is the guard against reaching back into a period that's already been
  // paid — rounding a settled period would re-price it after the fact.
  async function roundAll() {
    if (!roundable.length) return;
    // Belt and braces: `roundable` is already filtered, but this loop writes to the
    // shift record, so re-assert both guards before anything leaves the browser.
    if (roundable.some(r => (r.driver || '').toUpperCase() !== ROUND_LOGIN)) {
      addNotification(`Rounding is restricted to ${ROUND_LOGIN} — nothing was changed.`, 'error');
      return;
    }
    if (roundable.some(isSettled)) {
      addNotification(`Those wages are paid through ${paidThrough} — nothing was changed.`, 'error');
      return;
    }
    const span = (fromDate || toDate) ? `${fromDate || '…'} → ${toDate || '…'}` : `the last ${days || 'all'} days`;
    const who = roundable[0]?.name || ROUND_LOGIN;
    const msg = `Round ${roundable.length} of ${who}'s shift${roundable.length !== 1 ? 's' : ''} to the nearest 15 minutes?\n\n` +
      `Driver: ${who} (${ROUND_LOGIN}) — no other driver is affected\n` +
      `Range: ${span}\n` +
      `Hours are recalculated from the rounded times. This rewrites the saved shift record and can't be undone in bulk.`;
    if (!window.confirm(msg)) return;

    setBulkBusy({ done: 0, total: roundable.length });
    let ok = 0, failed = 0;
    const CONCURRENCY = 4;
    for (let i = 0; i < roundable.length; i += CONCURRENCY) {
      const batch = roundable.slice(i, i + CONCURRENCY);
      await Promise.all(batch.map(async (row) => {
        try {
          const rs = roundHHMMTo15(row.shiftStart) || row.shiftStart;
          const rf = roundHHMMTo15(row.finishTime) || row.finishTime;
          const res = await fetch('/.netlify/functions/appsscript', {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            // op:'round15' opts this write into the server-side rules (BOUVENGJ only,
            // open period only) — the guards above are the UI's, these are the real ones.
            body: JSON.stringify({ action: 'editShiftTimes', op: 'round15', submittedAt: row.submittedAt, startTime: rs, finishTime: rf, by: (currentUser?.login || 'ADMIN').toUpperCase() }),
          });
          const d = await res.json();
          if (!d.success) throw new Error(d.error || 'Round failed');
          ok++;
          setRows(prev => prev.map(r => r.submittedAt === row.submittedAt
            ? { ...r, shiftStart: d.shiftStart ?? rs, finishTime: d.finishTime ?? rf, totalHours: d.totalHours ?? r.totalHours }
            : r));
        } catch {
          failed++;
        } finally {
          setBulkBusy(b => (b ? { ...b, done: b.done + 1 } : b));
        }
      }));
    }
    setBulkBusy(null);
    if (failed) addNotification(`Rounded ${ok}, ${failed} failed — check and retry`, failed > ok ? 'error' : 'info');
    else addNotification(`Rounded ${ok} shift${ok !== 1 ? 's' : ''} to the nearest 15 min`, 'success');
  }

  async function reject() {
    const row = rejectModal.row;
    const reason = rejectModal.reason.trim();
    if (!reason) { addNotification('Please enter a reason', 'error'); return; }
    setActionLoading(a => ({ ...a, [row.submittedAt]: 'rejecting' }));
    try {
      const res = await fetch('/.netlify/functions/appsscript', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'rejectShift',
          submittedAt: row.submittedAt,
          reviewedBy: 'ADMIN',
          reason,
        }),
      });
      const data = await res.json();
      if (data.success) {
        addNotification(`${row.name} shift rejected`, 'info');
        setRows(prev => prev.map(r => r.submittedAt === row.submittedAt ? { ...r, status: 'REJECTED', managerNotes: reason } : r));
        setRejectModal({ open: false, row: null, reason: '' });
      } else {
        addNotification(data.error || 'Reject failed', 'error');
      }
    } catch {
      addNotification('Could not connect', 'error');
    } finally {
      setActionLoading(a => { const n = { ...a }; delete n[row.submittedAt]; return n; });
    }
  }

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div style={{ padding: '32px 28px', maxWidth: 1100, margin: '0 auto' }}>

      {/* Page header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 28 }}>
        <div>
          <h2 style={{ marginBottom: 4 }}>SHIFT APPROVALS</h2>
          <p style={{ color: 'var(--hp-dim)', fontSize: '0.875rem' }}>
            Review, approve, or reject driver shift submissions
          </p>
        </div>
        <button
          onClick={load}
          disabled={loading}
          style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'var(--hp-surface)', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', padding: '8px 16px', color: 'var(--hp-text)', cursor: 'pointer', fontSize: '0.875rem' }}
        >
          <RefreshCw size={14} style={{ animation: loading ? 'spin 1s linear infinite' : 'none' }} />
          Refresh
        </button>
      </div>

      {/* Stats row */}
      <div style={{ display: 'flex', gap: 16, marginBottom: 28, flexWrap: 'wrap' }}>
        <StatCard icon={Clock}          label="Pending Review"    value={pendingCount}   color="var(--hp-amber)" />
        <StatCard icon={CheckCircle}    label="Approved Today"    value={approvedToday}  color="var(--hp-green)" />
        <StatCard icon={ClipboardCheck} label="Amendments Pending" value={amendmentCount} color="#a78bfa" />
      </div>

      {/* ── Admin: add a shift for a driver (couldn't activate the portal) ── */}
      <AdminAddShift addNotification={addNotification} personnel={personnel} by={(currentUser?.login || '').toUpperCase()} onAdded={load} />

      {/* ── Admin: finish a shift for a driver ── */}
      <AdminFinishShift addNotification={addNotification} by={(currentUser?.login || '').toUpperCase()} onFinished={load} />

      {/* ── Admin: review and close a whole period's unfinished shifts ── */}
      <PeriodCloseout addNotification={addNotification} by={(currentUser?.login || '').toUpperCase()} onChanged={load} />

      {/* Repair missed shifts from the AP Scanner Audit file (server-side ingest) */}
      <ScanRepair addNotification={addNotification} by={(currentUser?.login || '').toUpperCase()} onRepaired={load} />

      {/* Portal logoff vs last scan + parcels-per-hour over a period */}
      <LogoffVariance addNotification={addNotification} />

      {/* ── Admin: attach a scanner photo for a driver ── */}
      <AdminScannerUpload personnel={personnel} addNotification={addNotification} />

      {/* ── Depot QR Codes panel ── */}
      <div style={{ marginBottom: 24, background: 'var(--hp-surface)', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius-lg)', overflow: 'hidden' }}>
        {/* Panel toggle header */}
        <button
          onClick={() => setQrOpen(o => !o)}
          style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 20px', background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'var(--font-body)' }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <QrCode size={16} color="var(--hp-gold)" />
            <span style={{ fontSize: '0.82rem', fontWeight: 700, color: 'var(--hp-text)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Depot QR Codes</span>
            <span style={{ fontSize: '0.75rem', color: 'var(--hp-dim)' }}>Print gate posters</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {qrOpen && depots.length > 0 && (
              <button
                onClick={e => { e.stopPropagation(); openPrintWindow(depots); }}
                style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '5px 12px', background: 'var(--hp-gold)', border: 'none', borderRadius: 'var(--radius)', color: 'var(--hp-black)', cursor: 'pointer', fontSize: '0.78rem', fontWeight: 700, fontFamily: 'var(--font-body)' }}
              >
                <Printer size={12} /> Print All
              </button>
            )}
            {qrOpen ? <ChevronUp size={16} color="var(--hp-dim)" /> : <ChevronDown size={16} color="var(--hp-dim)" />}
          </div>
        </button>

        {/* Expanded content */}
        {qrOpen && (
          <div style={{ borderTop: '1px solid var(--hp-border)', padding: '20px' }}>
            {qrLoading && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--hp-dim)', fontSize: '0.875rem' }}>
                <RefreshCw size={14} style={{ animation: 'spin 1s linear infinite' }} /> Loading QR codes…
              </div>
            )}
            {!qrLoading && depots.length === 0 && (
              <div style={{ color: 'var(--hp-dim)', fontSize: '0.875rem' }}>
                No depots configured. Set <code>DEPOT_QR_BOTANY</code> / <code>DEPOT_QR_ALEXANDRIA</code> in Netlify env vars.
              </div>
            )}
            {!qrLoading && depots.length > 0 && (
              <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
                {depots.map(depot => (
                  <div key={depot.id} style={{ background: 'var(--hp-card)', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', padding: '16px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, minWidth: 200 }}>
                    {/* QR image */}
                    <div style={{ background: '#fff', borderRadius: 8, padding: 8, border: '1px solid var(--hp-border)' }}>
                      <img
                        src={qrImageUrl(depot.url, 160)}
                        alt={`QR ${depot.name}`}
                        width={160} height={160}
                        style={{ display: 'block', borderRadius: 4 }}
                      />
                    </div>
                    {/* Depot info */}
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontWeight: 700, fontSize: '0.875rem', color: 'var(--hp-text)' }}>{depot.name}</div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--hp-dim)', marginTop: 2 }}>{depot.address}</div>
                    </div>
                    {/* Print single */}
                    <button
                      onClick={() => openPrintWindow([depot])}
                      style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '6px 14px', background: 'transparent', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', color: 'var(--hp-dim)', cursor: 'pointer', fontSize: '0.78rem', fontFamily: 'var(--font-body)', fontWeight: 500 }}
                    >
                      <Printer size={12} /> Print poster
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap', alignItems: 'center' }}>
        {/* Status filter tabs */}
        <div style={{ display: 'flex', gap: 4, background: 'var(--hp-surface)', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', padding: 4 }}>
          {['ALL', 'PENDING', 'APPROVED', 'REJECTED'].map(s => (
            <button
              key={s}
              onClick={() => setFilterStatus(s)}
              style={{
                padding: '5px 14px',
                borderRadius: 6,
                border: 'none',
                cursor: 'pointer',
                fontSize: '0.78rem',
                fontWeight: 600,
                fontFamily: 'var(--font-body)',
                background: filterStatus === s ? 'var(--hp-gold)' : 'transparent',
                color: filterStatus === s ? 'var(--hp-black)' : 'var(--hp-dim)',
                transition: 'all 150ms',
              }}
            >{s === 'ALL' ? 'All' : s.charAt(0) + s.slice(1).toLowerCase()}</button>
          ))}
        </div>

        {/* Driver search */}
        <input
          type="text"
          placeholder="Search driver..."
          value={filterDriver}
          onChange={e => setFilterDriver(e.target.value)}
          style={{ background: 'var(--hp-surface)', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', padding: '7px 12px', color: 'var(--hp-text)', fontSize: '0.875rem', outline: 'none', minWidth: 180, fontFamily: 'var(--font-body)' }}
        />

        {/* Date range picker */}
        <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
          <CalendarDays size={14} color="var(--hp-dim)" style={{ position: 'absolute', left: 10, pointerEvents: 'none', zIndex: 1 }} />
          <select
            value={days}
            onChange={e => setDays(Number(e.target.value))}
            style={{
              background: 'var(--hp-surface)',
              border: '1px solid var(--hp-border)',
              borderRadius: 'var(--radius)',
              padding: '7px 12px 7px 30px',
              color: 'var(--hp-text)',
              fontSize: '0.875rem',
              outline: 'none',
              fontFamily: 'var(--font-body)',
              cursor: 'pointer',
              minWidth: 180,
            }}
          >
            <optgroup label="Recent">
              <option value={7}>Last 7 days</option>
              <option value={14}>Last 14 days</option>
              <option value={28}>Last 28 days</option>
            </optgroup>
            <optgroup label="Extended">
              <option value={90}>Last 3 months</option>
              <option value={180}>Last 6 months</option>
              <option value={365}>Last 12 months</option>
            </optgroup>
            <optgroup label="Full history">
              <option value={0}>All records</option>
            </optgroup>
          </select>
        </div>

        {/* Pay-period range — pins the list to exact From/To dates (inclusive)
            and totals the hours below, for reconciling a monthly wage run. */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: '0.7rem', color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Period</span>
          <input
            type="date"
            value={fromDate}
            max={toDate || undefined}
            onChange={e => setFromDate(e.target.value)}
            style={{ background: 'var(--hp-surface)', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', padding: '6px 10px', color: 'var(--hp-text)', fontSize: '0.82rem', fontFamily: 'var(--font-body)', colorScheme: 'dark' }}
          />
          <span style={{ color: 'var(--hp-dim)', fontSize: '0.8rem' }}>→</span>
          <input
            type="date"
            value={toDate}
            min={fromDate || undefined}
            onChange={e => setToDate(e.target.value)}
            style={{ background: 'var(--hp-surface)', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', padding: '6px 10px', color: 'var(--hp-text)', fontSize: '0.82rem', fontFamily: 'var(--font-body)', colorScheme: 'dark' }}
          />
          <button
            onClick={() => {
              // The just-closed monthly period runs the 15th → the 14th.
              const now = new Date();
              const endBase = new Date(now.getFullYear(), now.getMonth(), 1);
              if (now.getDate() < 15) endBase.setMonth(endBase.getMonth() - 1);
              const start = new Date(endBase.getFullYear(), endBase.getMonth() - 1, 15);
              const end   = new Date(endBase.getFullYear(), endBase.getMonth(), 14);
              const iso = d => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
              setFromDate(iso(start));
              setToDate(iso(end));
            }}
            style={{ padding: '6px 10px', background: 'transparent', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', color: 'var(--hp-dim)', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--font-body)' }}
          >This pay period</button>
          {(fromDate || toDate) && (
            <button
              onClick={() => { setFromDate(''); setToDate(''); }}
              style={{ padding: '6px 10px', background: 'transparent', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', color: 'var(--hp-dim)', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--font-body)' }}
            >Clear</button>
          )}
        </div>

        {/* Off-grid shifts of hers that the lock protects — say so, so the button skipping
            them doesn't look like it missed them. */}
        {settledSkipped.length > 0 && (
          <span title={`Shifts on or before ${paidThrough} are covered by wages already paid, so they can't be re-priced`}
            style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: '0.72rem', color: 'var(--hp-dim)', fontFamily: 'var(--font-body)' }}>
            <AlertTriangle size={12} /> {settledSkipped.length} shift{settledSkipped.length !== 1 ? 's' : ''} not rounded (paid through {paidThrough})
          </span>
        )}

        {/* The lock itself — visible, and advanced by hand once a run has gone out. */}
        {paidThrough !== null && (
          <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.72rem', color: 'var(--hp-dim)', fontFamily: 'var(--font-body)' }}>
            <span>Wages paid through <strong style={{ color: 'var(--hp-text)', fontFamily: 'var(--font-mono)' }}>{paidThrough || 'not set'}</strong></span>
            {toDate && toDate !== paidThrough && (
              <button
                onClick={() => markPaidThrough(toDate)}
                disabled={paidBusy}
                title={`Lock ${ROUND_LOGIN}'s shifts up to ${toDate} against further rounding — do this once the run has been paid`}
                style={{ padding: '5px 10px', background: 'transparent', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', color: 'var(--hp-dim)', fontSize: '0.72rem', fontWeight: 600, cursor: paidBusy ? 'default' : 'pointer', opacity: paidBusy ? 0.6 : 1, fontFamily: 'var(--font-body)' }}
              >{paidBusy ? 'Saving…' : `Mark paid through ${toDate}`}</button>
            )}
          </span>
        )}

        {/* Snap BOUVENGJ's shifts on screen to the nearest quarter hour — hers only */}
        {roundable.length > 0 && (
          <button
            onClick={roundAll}
            disabled={!!bulkBusy}
            title={`Round ${ROUND_LOGIN}'s shifts shown to the nearest 15 minutes and recalculate hours — no other driver is affected`}
            style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', background: 'transparent', border: '1px solid var(--hp-gold)', borderRadius: 'var(--radius)', color: 'var(--hp-gold)', fontSize: '0.78rem', fontWeight: 700, cursor: bulkBusy ? 'default' : 'pointer', opacity: bulkBusy ? 0.6 : 1, fontFamily: 'var(--font-body)' }}
          >
            <Clock size={13} /> Round {roundable.length} to 15 · {ROUND_LOGIN}
          </button>
        )}

        {(() => {
          const pendingInView = filtered.filter(r => r.status === 'PENDING').length;
          if (!pendingInView && !bulkBusy) return null;
          return (
            <button
              onClick={approveAll}
              disabled={!!bulkBusy || !pendingInView}
              style={{
                marginLeft: 'auto',
                display: 'flex', alignItems: 'center', gap: 7,
                background: bulkBusy ? 'var(--hp-surface)' : 'var(--hp-green)',
                border: bulkBusy ? '1px solid var(--hp-border)' : 'none',
                borderRadius: 'var(--radius)',
                padding: '8px 16px',
                color: bulkBusy ? 'var(--hp-dim)' : '#fff',
                fontWeight: 700, fontSize: '0.85rem',
                fontFamily: 'var(--font-body)',
                cursor: bulkBusy ? 'default' : 'pointer',
              }}
            >
              <CheckCircle size={15} />
              {bulkBusy ? `Approving ${bulkBusy.done}/${bulkBusy.total}…` : `Approve all pending (${pendingInView})`}
            </button>
          );
        })()}

        <span style={{ color: 'var(--hp-dim)', fontSize: '0.8rem', marginLeft: bulkBusy || filtered.some(r => r.status === 'PENDING') ? 0 : 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
          {(fromDate || toDate) ? (
            <span style={{ color: 'var(--hp-dim)', fontSize: '0.75rem', fontFamily: 'var(--font-mono)' }}>
              {fromDate || '…'} → {toDate || '…'}
            </span>
          ) : days > 0 && (() => {
            const from = new Date(); from.setDate(from.getDate() - days);
            return (
              <span style={{ color: 'var(--hp-dim)', fontSize: '0.75rem', fontFamily: 'var(--font-mono)' }}>
                {from.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: '2-digit' })}
                {' → today'}
              </span>
            );
          })()}
          <span>{filtered.length} record{filtered.length !== 1 ? 's' : ''}</span>
          <span
            style={{ color: 'var(--hp-text)', fontWeight: 700, fontFamily: 'var(--font-mono)' }}
            title={`Payable hours: ${payableRows.length} shift${payableRows.length !== 1 ? 's' : ''} counted. Superseded and rejected rows are listed but not counted.`}
          >· {totalHours.toFixed(2)} hrs</span>
          {dupDays.length > 0 && (
            <span
              style={{ color: 'var(--hp-red, #e5484d)', fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 4 }}
              title={`Two live shifts on the same day — only the latest is counted, but these need fixing:\n${dupDays.join('\n')}`}
            >
              <AlertTriangle size={13} /> {dupDays.length} duplicate day{dupDays.length !== 1 ? 's' : ''}
            </span>
          )}
        </span>
      </div>

      {/* Table */}
      {/* The rows are a 670px grid: on a phone `overflow: hidden` cut off Status and the
          Approve/Reject buttons with no way to reach them, so this scrolls sideways. */}
      <div style={{ background: 'var(--hp-surface)', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius-lg)', overflowX: 'auto' }}>
        {/* Table header */}
        <div style={{ display: 'grid', gridTemplateColumns: '110px 140px 1fr 90px 90px 120px 120px', gap: 0, background: 'var(--hp-card)', borderBottom: '1px solid var(--hp-border)', padding: '10px 20px' }}>
          {['Date', 'Driver', 'Shift Times', 'Hours', 'KM', 'Status', 'Actions'].map(h => (
            <div key={h} style={{ fontSize: '0.7rem', fontWeight: 700, color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>{h}</div>
          ))}
        </div>

        {loading && (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--hp-dim)' }}>Loading...</div>
        )}

        {!loading && filtered.length === 0 && (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--hp-dim)' }}>
            No shifts found for this filter.
          </div>
        )}

        {!loading && filtered.map((row, i) => {
          const isExpanded = expandedRow === row.submittedAt;
          const isAmendment = row.type === 'AMENDMENT';
          const isActing = !!actionLoading[row.submittedAt];
          const statusStyle = STATUS_STYLE[row.status] || STATUS_STYLE.PENDING;

          return (
            <div key={row.submittedAt || i} style={{ borderBottom: i < filtered.length - 1 ? '1px solid var(--hp-border)' : 'none', background: isAmendment ? 'rgba(139,92,246,0.04)' : 'transparent' }}>
              {/* Main row */}
              <div
                style={{ display: 'grid', gridTemplateColumns: '110px 140px 1fr 90px 90px 120px 120px', gap: 0, padding: '14px 20px', alignItems: 'center', cursor: 'pointer' }}
                onClick={() => setExpandedRow(isExpanded ? null : row.submittedAt)}
              >
                {/* Date */}
                <div style={{ fontSize: '0.83rem', color: 'var(--hp-text)', fontFamily: 'var(--font-mono)' }}>
                  {row.date}
                </div>

                {/* Driver */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <DriverAvatar login={row.driver} photo={photos[(row.driver || '').toUpperCase()]} size={26} />
                  <div>
                    <div style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--hp-text)' }}>{row.name || row.driver}</div>
                    <div style={{ fontSize: '0.72rem', color: 'var(--hp-dim)', marginTop: 2 }}>{row.driver}</div>
                    {/* Visa-cap watch: fortnight hours (driving + night sort) vs the 48h cap. */}
                    {(() => {
                      const cw = capWatch[(row.driver || '').toUpperCase()];
                      if (!cw) return null;
                      const c = cw.over ? 'var(--hp-red)' : cw.warn ? '#b8860b' : 'var(--hp-dim)';
                      const tip = (cw.windows || []).map((w) =>
                        `${w.start} – ${w.end}: ${(w.counted != null ? w.counted : w.total).toFixed(1)}h counted${w.breakHours ? ` + ${w.breakHours.toFixed(1)}h course break` : ''} (drive ${w.driver.toFixed(1)} + sort ${w.sorter.toFixed(1)})`).join('\n');
                      return (
                        <div style={{ fontSize: '0.66rem', fontWeight: 800, color: c, marginTop: 2 }}
                          title={`Visa work cap — hours this fortnight (both Monday windows)\n${tip}`}>
                          {cw.onBreak ? `Course break · unlimited (visa cap resumes after ${cw.currentBreak.end})` : `Visa cap ${cw.maxTotal.toFixed(1)}h / ${cw.capHours}h`}
                        </div>
                      );
                    })()}
                  </div>
                </div>

                {/* Shift times */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: '0.83rem', color: 'var(--hp-text)' }}>{row.shiftStart || '—'}</span>
                  {row.breakStart && <span style={{ fontSize: '0.7rem', color: 'var(--hp-amber)', background: 'rgba(245,158,11,0.1)', padding: '2px 6px', borderRadius: 4 }}>
                    Break {row.breakStart}–{row.breakEnd}
                  </span>}
                  {row.finishTime && <>
                    <span style={{ color: 'var(--hp-dim)', fontSize: '0.75rem' }}>→</span>
                    <span style={{ fontFamily: 'var(--font-mono)', fontSize: '0.83rem', color: 'var(--hp-text)' }}>{row.finishTime}</span>
                  </>}
                  {isAmendment && <Badge style={TYPE_STYLE.AMENDMENT} text="Amendment" />}
                </div>

                {/* Hours */}
                <div style={{ fontFamily: 'var(--font-mono)', fontSize: '0.875rem', color: 'var(--hp-text)' }}>
                  {row.totalHours ? `${Number(row.totalHours).toFixed(2)}h` : '—'}
                </div>

                {/* KM */}
                <div style={{ fontFamily: 'var(--font-mono)', fontSize: '0.875rem', color: 'var(--hp-text)' }}>
                  {row.totalKm ? `${row.totalKm} km` : '—'}
                </div>

                {/* Status */}
                <div><Badge style={statusStyle} /></div>

                {/* Actions */}
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }} onClick={e => e.stopPropagation()}>
                  {row.status === 'PENDING' && (
                    <>
                      <button
                        title="Approve"
                        disabled={isActing}
                        onClick={() => approve(row)}
                        style={{ background: 'rgba(34,197,94,0.15)', border: '1px solid rgba(34,197,94,0.3)', borderRadius: 6, padding: '5px 10px', cursor: 'pointer', color: 'var(--hp-green)', display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.78rem', fontWeight: 600, fontFamily: 'var(--font-body)' }}
                      >
                        <CheckCircle size={14} /> Approve
                      </button>
                      <button
                        title="Reject"
                        disabled={isActing}
                        onClick={() => setRejectModal({ open: true, row, reason: '' })}
                        style={{ background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 6, padding: '5px 10px', cursor: 'pointer', color: 'var(--hp-red)', display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.78rem', fontWeight: 600, fontFamily: 'var(--font-body)' }}
                      >
                        <XCircle size={14} /> Reject
                      </button>
                    </>
                  )}
                  {row.status !== 'PENDING' && (
                    <span style={{ fontSize: '0.75rem', color: 'var(--hp-dim)' }}>
                      {row.reviewedBy && `by ${row.reviewedBy}`}
                    </span>
                  )}
                  <button
                    onClick={() => setExpandedRow(isExpanded ? null : row.submittedAt)}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--hp-dim)', padding: 2 }}
                  >
                    {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                  </button>
                </div>
              </div>

              {/* Expanded detail row */}
              {isExpanded && (
                <div style={{ padding: '0 20px 16px', borderTop: '1px solid var(--hp-border)', background: 'var(--hp-card)' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(180px, 100%), 1fr))', gap: 12, paddingTop: 14 }}>
                    {[
                      ['Shift Start',  row.shiftStart  || '—'],
                      ['Break Start',  row.breakStart  || 'No break'],
                      ['Break End',    row.breakEnd    || '—'],
                      ['Finish Time',  row.finishTime  || '—'],
                      ['Vehicle',      row.vehicle     || '—'],
                      ['Odo Start',    row.odoStart    ? `${row.odoStart} km` : '—'],
                      ['Odo End',      row.odoEnd      ? `${row.odoEnd} km`   : '—'],
                      ['Total KM',     row.totalKm     ? `${row.totalKm} km`  : '—'],
                      ['Total Hours',  row.totalHours  ? `${row.totalHours} hrs` : '—'],
                      ['Submitted',    row.submittedAt ? new Date(row.submittedAt).toLocaleString('en-AU') : '—'],
                    ].map(([label, value]) => (
                      <div key={label}>
                        <div style={{ fontSize: '0.68rem', color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 600, marginBottom: 3 }}>{label}</div>
                        <div style={{ fontSize: '0.875rem', color: 'var(--hp-text)', fontFamily: /km|hrs|Start|End|Time|Finish/.test(label) ? 'var(--font-mono)' : 'var(--font-body)' }}>{value}</div>
                      </div>
                    ))}
                  </div>

                  {/* Edit finish time / odometer (recomputes hours + km) */}
                  {editRow === row.submittedAt ? (
                    <div style={{ marginTop: 14, padding: 14, border: '1px solid var(--hp-gold)', borderRadius: 'var(--radius)', background: 'rgba(201,168,76,0.06)' }}>
                      <div style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--hp-text)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>Edit start / finish / break / odometer</div>
                      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                        <div>
                          <div style={{ fontSize: '0.68rem', color: 'var(--hp-dim)', marginBottom: 5 }}>Start time</div>
                          <input type="time" value={editStart} onChange={e => setEditStart(e.target.value)}
                            style={{ background: 'var(--hp-bg)', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', padding: '8px 10px', color: 'var(--hp-text)', fontSize: '0.88rem' }} />
                        </div>
                        <div>
                          <div style={{ fontSize: '0.68rem', color: 'var(--hp-dim)', marginBottom: 5 }}>Finish time</div>
                          <input type="time" value={editFinish} onChange={e => setEditFinish(e.target.value)}
                            style={{ background: 'var(--hp-bg)', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', padding: '8px 10px', color: 'var(--hp-text)', fontSize: '0.88rem' }} />
                        </div>
                        <div>
                          <div style={{ fontSize: '0.68rem', color: 'var(--hp-dim)', marginBottom: 5 }}>Break start</div>
                          <input type="time" value={editBreakStart} onChange={e => setEditBreakStart(e.target.value)}
                            style={{ background: 'var(--hp-bg)', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', padding: '8px 10px', color: 'var(--hp-text)', fontSize: '0.88rem' }} />
                        </div>
                        <div>
                          <div style={{ fontSize: '0.68rem', color: 'var(--hp-dim)', marginBottom: 5 }}>Break end</div>
                          <input type="time" value={editBreakEnd} onChange={e => setEditBreakEnd(e.target.value)}
                            style={{ background: 'var(--hp-bg)', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', padding: '8px 10px', color: 'var(--hp-text)', fontSize: '0.88rem' }} />
                        </div>
                        {(editBreakStart || editBreakEnd) && (
                          <button
                            onClick={() => { setEditBreakStart(''); setEditBreakEnd(''); }}
                            disabled={editBusy}
                            title="Clear the break entirely — the shift is paid start to finish"
                            style={{ padding: '9px 14px', background: 'transparent', border: '1px solid var(--hp-red)', borderRadius: 'var(--radius)', color: 'var(--hp-red)', fontWeight: 700, fontSize: '0.82rem', cursor: 'pointer' }}>
                            ✕ Remove break
                          </button>
                        )}
                        <div>
                          <div style={{ fontSize: '0.68rem', color: 'var(--hp-dim)', marginBottom: 5 }}>Finish odometer</div>
                          <input type="number" inputMode="numeric" value={editOdo} onChange={e => setEditOdo(e.target.value)} placeholder="km"
                            style={{ width: 120, background: 'var(--hp-bg)', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', padding: '8px 10px', color: 'var(--hp-text)', fontSize: '0.88rem' }} />
                        </div>
                        <button
                          onClick={() => { setEditStart(s => roundHHMMTo15(s) || s); setEditFinish(f => roundHHMMTo15(f) || f); }}
                          disabled={editBusy}
                          title="Snap these times to the nearest 15 minutes — review, then Save"
                          style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '9px 14px', background: 'transparent', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', color: 'var(--hp-text)', fontWeight: 700, fontSize: '0.82rem', cursor: 'pointer' }}>
                          <Clock size={13} /> Round to 15
                        </button>
                        <button onClick={() => saveEdit(row)} disabled={editBusy}
                          style={{ padding: '9px 18px', background: 'var(--hp-gold)', border: 'none', borderRadius: 'var(--radius)', color: 'var(--hp-black)', fontWeight: 800, fontSize: '0.82rem', cursor: editBusy ? 'not-allowed' : 'pointer', opacity: editBusy ? 0.6 : 1 }}>
                          {editBusy ? 'Saving…' : 'Save changes'}
                        </button>
                        <button onClick={() => setEditRow(null)} disabled={editBusy}
                          style={{ padding: '9px 14px', background: 'var(--hp-surface)', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', color: 'var(--hp-dim)', fontWeight: 700, fontSize: '0.82rem', cursor: 'pointer' }}>
                          Cancel
                        </button>
                      </div>
                      <div style={{ fontSize: '0.72rem', color: 'var(--hp-dim)', marginTop: 8 }}>Hours recalculate from start to finish minus the break; km from odo start to finish. Clear both break fields (or Remove break) to pay the shift with no break.</div>
                    </div>
                  ) : (
                    <button onClick={() => { setEditRow(row.submittedAt); setEditStart(row.shiftStart || ''); setEditFinish(row.finishTime || ''); setEditBreakStart(row.breakStart || ''); setEditBreakEnd(row.breakEnd || ''); setEditOdo(row.odoEnd || ''); }}
                      style={{ marginTop: 12, display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 14px', background: 'var(--hp-surface)', border: '1px solid var(--hp-gold)', borderRadius: 'var(--radius)', color: 'var(--hp-gold)', fontWeight: 700, fontSize: '0.78rem', cursor: 'pointer' }}>
                      ✎ Edit start / finish / break / odometer
                    </button>
                  )}

                  {/* Amendment reason */}
                  {isAmendment && row.amendmentReason && (
                    <div style={{ marginTop: 14, background: 'rgba(139,92,246,0.1)', border: '1px solid rgba(139,92,246,0.25)', borderRadius: 'var(--radius)', padding: '10px 14px' }}>
                      <div style={{ fontSize: '0.7rem', color: '#a78bfa', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 4 }}>Amendment Reason</div>
                      <div style={{ fontSize: '0.875rem', color: 'var(--hp-text)' }}>{row.amendmentReason}</div>
                    </div>
                  )}

                  {/* Manager notes — a rejection reason, EXCEPT when the driver
                      finished without their scanner photo because the camera on
                      their phone would not take one. That is a job for the office
                      (chase the photo, attach it above), not a rejection, so it is
                      labelled and coloured as its own thing. Marker written by the
                      portal — see NO_PHOTO_PREFIX in DriverLogPage. */}
                  {row.managerNotes && (isNoPhotoNote(row.managerNotes) ? (
                    <div style={{ marginTop: 10, background: 'rgba(245,158,11,0.09)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: 'var(--radius)', padding: '10px 14px' }}>
                      <div style={{ fontSize: '0.7rem', color: 'var(--hp-amber)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 4 }}>No scanner photo — camera fault</div>
                      <div style={{ fontSize: '0.875rem', color: 'var(--hp-text)' }}>{row.managerNotes.slice(NO_PHOTO_PREFIX.length)}</div>
                      <div style={{ fontSize: '0.78rem', color: 'var(--hp-dim)', marginTop: 6 }}>
                        Ask the driver to send the photo, then attach it with “Attach scanner photo for a driver” above.
                      </div>
                    </div>
                  ) : (
                    <div style={{ marginTop: 10, background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 'var(--radius)', padding: '10px 14px' }}>
                      <div style={{ fontSize: '0.7rem', color: 'var(--hp-red)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 4 }}>Manager Notes</div>
                      <div style={{ fontSize: '0.875rem', color: 'var(--hp-text)' }}>{row.managerNotes}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Reject Modal */}
      {rejectModal.open && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }}>
          <div style={{ background: 'var(--hp-surface)', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius-lg)', padding: 28, maxWidth: 420, width: '100%' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
              <AlertTriangle size={20} color="var(--hp-red)" />
              <h4 style={{ fontFamily: 'var(--font-display)', fontSize: '1.2rem', letterSpacing: '0.04em' }}>REJECT SHIFT</h4>
            </div>
            <p style={{ color: 'var(--hp-dim)', fontSize: '0.875rem', marginBottom: 6 }}>
              Rejecting shift for <strong style={{ color: 'var(--hp-text)' }}>{rejectModal.row?.name}</strong> on <strong style={{ color: 'var(--hp-text)' }}>{rejectModal.row?.date}</strong>
            </p>
            <p style={{ color: 'var(--hp-dim)', fontSize: '0.8rem', marginBottom: 16 }}>
              The driver will be notified and can resubmit a corrected entry.
            </p>
            <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>
              Reason for rejection *
            </label>
            <textarea
              autoFocus
              rows={3}
              placeholder="e.g. Odometer reading appears incorrect — please recheck and resubmit"
              value={rejectModal.reason}
              onChange={e => setRejectModal(m => ({ ...m, reason: e.target.value }))}
              style={{ width: '100%', background: 'var(--hp-card)', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', color: 'var(--hp-text)', fontSize: '0.875rem', padding: '10px 12px', outline: 'none', resize: 'vertical', fontFamily: 'var(--font-body)', marginBottom: 16 }}
            />
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button
                onClick={() => setRejectModal({ open: false, row: null, reason: '' })}
                style={{ background: 'var(--hp-muted)', border: 'none', borderRadius: 'var(--radius)', padding: '9px 18px', color: 'var(--hp-text)', cursor: 'pointer', fontFamily: 'var(--font-body)', fontWeight: 600 }}
              >Cancel</button>
              <button
                onClick={reject}
                disabled={!rejectModal.reason.trim() || !!actionLoading[rejectModal.row?.submittedAt]}
                style={{ background: 'var(--hp-red)', border: 'none', borderRadius: 'var(--radius)', padding: '9px 18px', color: '#fff', cursor: 'pointer', fontFamily: 'var(--font-body)', fontWeight: 600, opacity: !rejectModal.reason.trim() ? 0.5 : 1 }}
              >Confirm Rejection</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
